// src/modules/rituales.js — vista de rituales del grupo (arengas + cábalas +
// promesas) + chat del grupo.
//
// FASE 4 · F1.3.d1. Migrado de localStorage a Supabase Postgres: arengas,
// cabalas, promesas, reacciones_ritual, arengas_guardadas y mensajes_grupo
// viven en la base. `renderRitualesGrupo` es async (carga → render).
//
// REGLA DEL BLOQUE: el HTML / CSS / layout NO se rediseña. Solo cambia la capa
// de datos — donde el código leía/escribía localStorage ahora usa
// core/rituales.js y core/chat.js. Las filas de Postgres se ADAPTAN al shape
// que el HTML-gen ya esperaba (igual que getPactoByGroup en F1.3.c1), así el
// render queda intacto.
//
// Discrepancia spec/realidad (Schema v1.8): los 8 tipos de arenga canónicos
// (cantico/declaracion/meme/cabreo/euforica/humor/mistica/otro) reemplazan las
// keys del mock viejo; `arengaTypes` es un Proxy para que el HTML-gen legacy
// (con su fallback `|| arengaTypes.vamos`) no haya que tocarlo.
//
// Chat: panel colapsable nuevo (spec Chat v1.0 §1), arriba de las cards de
// arengas, en el lenguaje visual del módulo.

import { getDB } from '../core/db.js'
import { getCurrentUser } from '../core/auth.js'
import { getMembership } from '../core/groups.js'
import {
    getArengasDelGrupo, createArenga, getReaccionesPorRituales, reaccionar, quitarReaccion,
    getCabalasDelGrupo, createCabala,
    getPromesasDelGrupo, createPromesa, declararPromesa, marcarCumplida, extenderPromesa,
    guardarArenga, quitarArengaGuardada, getArengasGuardadasDelUsuario,
    eliminarArengaCompleta, invalidarCacheRituales,
    getMisReacciones, getMisAlPasto, puntosPorRitual, refrescarContadoresRitual,
} from '../core/rituales.js'
import { getMensajesDelGrupo, enviarMensaje } from '../core/chat.js'
import { getTribunaLibre } from '../core/tribuna.js'
import { startRecording, getMaxDuration, extensionForMime } from '../core/audio-recorder.js'
import { uploadArenga, generateAudioId, buildShareUrl } from '../core/storage-arengas.js'
import { ensureBlobUrl, revokeBlobUrl } from '../core/audio-player.js'
import { computeReactionDelta, invertDeltas, applyReactionDelta } from '../core/reaction-delta.js'
import { renderActiveGroupBar, renderBottomNav } from '../ui/components.js'
import { renderPactoIcon } from '../ui/icons.js'
import { openShareModal } from '../ui/share-modal.js'
import { appRoot } from '../utils/dom.js'
import { formatTimeAgo } from '../utils/time-ago.js'

// Errores crudos de los RPCs / INSERTs de rituales → mensajes de UX.
const traducirErrorRitual = (error) => {
    const m = (error?.message || '').toLowerCase()
    if (m.includes('pacto_no_certificado')) return 'El pacto del grupo todavía no está certificado: solo se permiten arengas privadas.'
    if (m.includes('no_sos_miembro')) return 'No sos miembro de este grupo.'
    if (m.includes('no_sos_este_jugador')) return 'No pudimos identificar tu jugador en el grupo.'
    if (m.includes('no_te_validas_solo')) return 'No podés validar tu propia promesa — la valida otro integrante.'
    if (m.includes('promesa_cerrada')) return 'Esa promesa ya está cerrada.'
    if (m.includes('no_sos_del_grupo')) return 'Esa promesa es de otro grupo.'
    if (m.includes('ritual_inexistente')) return 'No encontramos ese ritual.'
    if (m.includes('arenga_inexistente')) return 'Esa arenga ya no existe.'
    if (m.includes('no_sos_el_creador')) return 'Solo el creador puede borrar esta arenga.'
    if (m.includes('mensaje_vacio')) return 'El mensaje está vacío.'
    if (m.includes('no_autenticado')) return 'Tu sesión expiró. Recargá la página.'
    if (m.includes('23514') || m.includes('check')) return 'Algún dato no cumple el formato permitido.'
    return 'No pudimos completar la acción. Probá de nuevo.'
}

// Escape de HTML para contenido de usuario en la UI nueva (chat).
const escHtml = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
))

// Decide cómo re-renderizar la vista de rituales tras una acción (crear /
// resolver / validar / extender / eliminar / chat). En el HOME la vista se
// monta EMBEBIDA por el guardian (options.target = <section> del home) y luego
// éste le saca la flecha ← y re-cablea los botones. Re-renderizar con
// renderRitualesGrupo(codigo, tab) a secas pintaba la vista AISLADA en
// #app-root: pisaba el home (Próximos partidos desaparecían) y dejaba el hash
// en /g/<codigo> mientras mostraba la vista aislada → la flecha ← (que hace
// hash='/g/<codigo>') no disparaba hashchange y "no hacía nada" hasta cambiar
// de tab. Cuando estamos embebidos delegamos en el re-embed del guardian (hook
// global) para mantener el home montado. Exportada para test.
export const reRenderTarget = (options) =>
  (options?.target && typeof window !== 'undefined' && typeof window.__pmReembedRituales === 'function')
    ? 'reembed'
    : 'render';

export const renderRitualesGrupo = async (codigo, activeTab = 'arengas', options = {}) => {
    // Bug #1: el guardian embebe esta vista en el home pasando options.target
    // (un <section> del home) — así renderiza sin pisar #app-root. Sin target,
    // pinta en appRoot como siempre (ruta /rituales/<codigo>). skipLoadingState
    // omite el "Cargando…" cuando el caller ya muestra su propio skeleton.
    const root = options.target || appRoot;
    // Re-render post-acción que PRESERVA el contexto de montaje (embebido vs
    // aislado). Reemplaza los renderRitualesGrupo(codigo, tab) directos que
    // pisaban el home al crear/resolver desde el embed (BUG-1/2/3). Embebido →
    // re-embed del guardian (hook); aislado → render directo en appRoot.
    const reRenderRituales = (tabDestino) =>
        reRenderTarget(options) === 'reembed'
            ? window.__pmReembedRituales(codigo, tabDestino)
            : renderRitualesGrupo(codigo, tabDestino, options.target ? options : undefined);
    // 3.11 — DT-3: limpia cualquier <section class="home-rituales"> colgada del DOM.
    document.querySelectorAll('section.home-rituales').forEach(s => s.remove());
    const grupo = getDB().grupos[codigo];
    if (!grupo) { window.location.hash = '/ingreso'; return; }
    const tabs = ['arengas', 'cabalas', 'promesas'];
    const tab = tabs.includes(activeTab) ? activeTab : 'arengas';
    const groupId = grupo.group_id;
    const currentUser = getCurrentUser();

    // Jugador activo del grupo (cache F1.3.b). Fallback defensivo al primero.
    const jugadorActivo = getMembership(codigo) || grupo.jugadores?.[0] || { id: 'yo', user_id: 'yo', apodo: 'Vos' };
    const now = Date.now();

    // BUG 2 fix: al abrir el detalle de una arenga (?arenga=ID), invalidar el
    // cache de reacciones para traer datos frescos. Sin esto, si otro jugador
    // reportó Al pasto a esta arenga mientras teníamos la cache caliente
    // (TTL 60s), el creador veía el contador stale al abrir su propio detalle.
    const _preflightParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (_preflightParams.get('arenga')) invalidarCacheRituales();

    // Estado de carga mientras se resuelven las lecturas contra Postgres.
    // Se omite cuando el caller ya muestra su propio placeholder (skeleton).
    if (!options.skipLoadingState) {
        root.innerHTML = `<div class="view-enter" style="width:100%;padding:3.4rem 1rem;text-align:center;color:var(--text-muted);">Cargando rituales&hellip;</div>`;
    }

    // ── Tipos de arenga: vocabulario CANÓNICO del Schema v1.8 (CHECK actualizado
    // 27-may-2026 alineado a spec arengas v1.2 §2). Los SVG paths se reciclan
    // de las 8 keys viejas (cantico/meme/declaracion/cabreo/euforica/humor/
    // mistica/otro) con remapeo pragmático:
    //   - vamos       = path de "cantico" viejo (cuerno → grito de aliento)
    //   - opinion     = path de "otro" viejo (bocadillo genérico)
    //   - sufrimiento = path de "mistica" viejo (formas dinámicas → emoción intensa)
    //   - humor       = path de "humor" viejo (sonrisa, mantenido)
    //   - festejo     = path de "euforica" viejo (estrellas brillantes)
    //   - canticos    = path de "meme" viejo (cara → canto colectivo)
    //   - bronca      = path de "cabreo" viejo (cara enojada, mantenido natural)
    //   - declaracion = path de "declaracion" viejo (bocadillo declamatorio)
    // arengaTypes es un Proxy: cualquier key desconocida cae al primer tipo
    // (vamos) para que el HTML-gen legacy con `|| arengaTypes.vamos` siga OK.
    const arengaTypesBase = {
        vamos:       { icon: 'M9 18V5l9-2v13M9 9l9-2M9 18a2 2 0 1 1-2-2 2 2 0 0 1 2 2ZM18 16a2 2 0 1 1-2-2 2 2 0 0 1 2 2Z', label: '¡Vamos todavía!', color: '#2563eb' },
        opinion:     { icon: 'M8 14c1.8-3.2 3.3-4.7 5.2-4.7h1.9c1.3 0 2.4 1.1 2.4 2.4v.6h-3.2M8 14l-2.2 3.2M8 14h6.7c1.5 0 2.8 1.2 2.8 2.8V18H9.6M12.5 9.4V6.8c0-1.3 1-2.3 2.3-2.3h.8v4.8', label: 'Opinión de partido', color: '#38bdf8' },
        sufrimiento: { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7.1l3.1 2.25-1.2 3.65h-3.8L8.9 9.35 12 7.1ZM12 7.1V4.2M15.1 9.35l2.9-1.2M13.9 13l1.85 2.85M10.1 13l-1.85 2.85M8.9 9.35 6 8.15M6.2 16.5l2.05-.65M17.8 16.5l-2.05-.65M4.2 11.6 6 8.15M19.8 11.6 18 8.15M7.4 18.8l.85-2.95M16.6 18.8l-.85-2.95', label: 'Sufrimiento', color: '#8b5cf6' },
        humor:       { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z', label: 'Humor', color: '#f59e0b' },
        festejo:     { icon: 'M6 20l3-10 5 5-8 5ZM14 4l.4 2.4L17 7l-2.6.6L14 10l-.4-2.4L11 7l2.6-.6L14 4ZM18 12l.3 1.7L20 14l-1.7.3L18 16l-.3-1.7L16 14l1.7-.3L18 12ZM7 5l.3 1.7L9 7l-1.7.3L7 9l-.3-1.7L5 7l1.7-.3L7 5Z', label: 'Festejo/Fiesta', color: '#22c55e' },
        canticos:    { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z', label: 'Cánticos', color: '#4f46e5' },
        bronca:      { icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9l2 1M16 9l-2 1M8.5 16c1.8-1.4 5.2-1.4 7 0M9 12h.1M15 12h.1', label: 'Bronca/Desilusión', color: '#991b1b' },
        declaracion: { icon: 'M5 6h14v9H9l-4 4V6ZM8 9h8M8 12h6M17 3l1 2M21 7l-2 1M7 3 6 2', label: 'Declaración', color: '#fbbf24' },
    };
    const arengaTypes = new Proxy(arengaTypesBase, { get: (t, k) => t[k] || t.vamos });

    // ── Arengas: lectura + adaptación al shape legacy del HTML-gen.
    const arengasRaw = await getArengasDelGrupo(groupId);
    const reaccionesArengas = await getReaccionesPorRituales('arenga', arengasRaw.map(a => a.arenga_id));
    const contarReaccArenga = (id, tipo) => reaccionesArengas.filter(r => r.ritual_id === id && r.tipo_reaccion === tipo).length;
    // Mi reacción real (no 'al_pasto') a cada arenga — para marcar visualmente
    // el botón activo y resolver el toggle-off de la spec v1.2 §10.
    const miReaccionPorArenga = (arengaId) => {
        if (!jugadorActivo?.id) return null;
        const r = reaccionesArengas.find(x =>
            x.ritual_id === arengaId &&
            x.player_id === jugadorActivo.id &&
            x.tipo_reaccion !== 'al_pasto'
        );
        return r?.tipo_reaccion || null;
    };
    // Spec arengas v1.2 §9: 1 reporte al_pasto por jugador. La DB ya tiene el
    // UNIQUE constraint que rechaza el duplicado, pero el optimistic del front
    // sumaba +1 cada vez (sin chequear estado previo) → contador inflado vs
    // realidad. Pre-computamos qué arengas ya reporté para que el botón
    // arranque en data-active='1' y el handler bloquee clicks repetidos.
    const miAlPastoPorArenga = (arengaId) => {
        if (!jugadorActivo?.id) return false;
        return reaccionesArengas.some((x) =>
            x.ritual_id === arengaId &&
            x.player_id === jugadorActivo.id &&
            x.tipo_reaccion === 'al_pasto'
        );
    };
    const arengas = arengasRaw.map(a => {
        const segundos = a.duracion_segundos || 0;
        return {
            id: a.arenga_id,
            audioId: a.audio_id,
            audioUrl: a.audio_url && a.audio_url !== 'pendiente' ? a.audio_url : '',
            tipo: a.tipo_arenga,
            titulo: a.titulo || '',
            duracion: `0:${String(segundos).padStart(2, '0')}`,
            segundos,
            creadorId: a.creador_id,
            creador: a.jugadores?.display_name || 'Jugador',
            grupoOrigen: grupo.nombre,
            visibilidad: a.es_publica ? 'publica' : 'grupo',
            createdAt: new Date(a.created_at).getTime(),
            escuchar: contarReaccArenga(a.arenga_id, 'escuchar'),
            pego: contarReaccArenga(a.arenga_id, 'pego'),
            loco: contarReaccArenga(a.arenga_id, 'loco'),
            pasto: contarReaccArenga(a.arenga_id, 'al_pasto'),
            miReaccion: miReaccionPorArenga(a.arenga_id),
            miAlPasto: miAlPastoPorArenga(a.arenga_id),
        };
    });
    const arengaReactionTotal = (a, reaction) => reaction === 'pasto'
        ? (a.pasto || 0)
        : (a[`${reaction}Grupo`] || 0) + (a[`${reaction}Publico`] || 0) + (a[reaction] || 0);
    const arengaTotalReactions = (a) => ['escuchar', 'pego', 'loco', 'pasto'].reduce((total, reaction) => total + arengaReactionTotal(a, reaction), 0);
    // Spec arengas v1.2 §5: tiempo "hace XX" obligatorio en card cerrada y
    // despliegue. Helper compartido (src/utils/time-ago.js) — antes era un
    // closure duplicado entre rituales.js y tribuna.js (DT cerrada).
    const arengaCreatedAgo = (createdAt) => formatTimeAgo(createdAt);
    const guardadasRaw = currentUser ? await getArengasGuardadasDelUsuario(currentUser.user_id) : [];
    const savedArengaSet = new Set(guardadasRaw.map(g => g.arenga_id));
    const isArengaSaved = (id) => savedArengaSet.has(id);
    const isOwnArenga = (a) => a.creadorId?.toString() === jugadorActivo.id?.toString() || a.creadorId?.toString() === jugadorActivo.user_id?.toString();
    const arengaParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const selectedArengaId = arengaParams.get('arenga');
    const isCreatingArenga = arengaParams.get('crear') === '1';
    const selectedArenga = arengas.find(a => a.id === selectedArengaId);
    const reactionDefs = [
        { id: 'escuchar', label: 'Escuchen esto', icon: 'M6 12h7l5-7v14l-5-7H6ZM4 9v6', color: '#75aadb' },
        { id: 'pego', label: 'Vamo arriba !', icon: 'M12 3c3 3 4 5.5 2 8 2-.5 3-2 3-4 2 2 3 4.4 3 7a8 8 0 1 1-16 0c0-3 1.4-5.4 4-7-.2 2.4.7 4 2.5 5C9.8 8.5 10.5 5.5 12 3Z', color: '#fb923c' },
        { id: 'loco', label: 'Jaja !', icon: 'M7 10h10M8 14c2.4 2.2 5.6 2.2 8 0M8.5 8h.1M15.5 8h.1M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', color: '#facc15' },
        { id: 'pasto', label: 'Te fuiste al pasto', icon: 'M5 18h9c2.4 0 4-1.6 4-4v-1h-5l-2-6H8l1.8 6H5v5Z', color: '#c084fc' }
    ];
    const renderVisibilityBadge = (visibilidad) => {
        const isPublic = visibilidad === 'publica';
        const icon = isPublic ? 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.6 9h16.8M3.6 15h16.8M12 3c2.1 2.4 3.2 5.4 3.2 9s-1.1 6.6-3.2 9c-2.1-2.4-3.2-5.4-3.2-9S9.9 5.4 12 3Z' : 'M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6v-9Z';
        return `<span style="display:inline-flex;align-items:center;gap:.32rem;border-radius:999px;padding:.24rem .5rem;border:1px solid ${isPublic ? 'rgba(34,197,94,.24)' : 'rgba(117,170,219,.24)'};background:${isPublic ? 'rgba(34,197,94,.08)' : 'rgba(117,170,219,.08)'};color:${isPublic ? '#86efac' : 'var(--celeste)'};font-size:.66rem;font-weight:900;">${renderPactoIcon(icon, isPublic ? '#86efac' : '#75aadb', 12)} ${isPublic ? 'Pública' : 'Privada'}</span>`;
    };
    const durationOptions = [
        { label: '2 horas', ms: 2 * 60 * 60 * 1000 },
        { label: '3 horas', ms: 3 * 60 * 60 * 1000 },
        { label: '6 horas', ms: 6 * 60 * 60 * 1000 },
        { label: '12 horas', ms: 12 * 60 * 60 * 1000 },
        { label: '1 día', ms: 24 * 60 * 60 * 1000 },
        { label: '1 semana', ms: 7 * 24 * 60 * 60 * 1000 },
        { label: 'Hasta el final del mundial', ms: 45 * 24 * 60 * 60 * 1000 }
    ];

    // ── Cábalas: lectura + adaptación. Visibilidad DB 'privada' → mock 'grupo'.
    // Auditoría 28-may: contadores eran hardcoded a 0 y botones sin handler.
    // Ahora cargamos reacciones reales + miReaccion para 1-por-jugador.
    // Doc Rituales Cábalas v1.0 §5: 2 reacciones (Bendita sea / Me sumo),
    // 1 por jugador eligiendo una. Slugs internos alineados al doc (DT-EM
    // cerrada 28-may): 'bendita_sea' / 'me_sumo'.
    const cabalasRaw = await getCabalasDelGrupo(groupId);
    const reaccionesCabalas = await getReaccionesPorRituales('cabala', cabalasRaw.map(c => c.cabala_id));
    const contarReaccCabala = (id, tipo) => reaccionesCabalas.filter(r => r.ritual_id === id && r.tipo_reaccion === tipo).length;
    const miReaccionPorCabala = (cabalaId) => {
        if (!jugadorActivo?.id) return null;
        const r = reaccionesCabalas.find(x => x.ritual_id === cabalaId && x.player_id === jugadorActivo.id);
        return r?.tipo_reaccion || null;
    };
    const cabalas = cabalasRaw.map(c => ({
        id: c.cabala_id,
        texto: c.texto,
        creadorId: c.creador_id,
        creador: c.jugadores?.display_name || 'Jugador',
        grupoOrigen: grupo.nombre,
        visibilidad: c.visibilidad === 'privada' ? 'grupo' : c.visibilidad,
        createdAt: new Date(c.created_at).getTime(),
        expiresAt: new Date(c.expira_en).getTime(),
        bendiciones: contarReaccCabala(c.cabala_id, 'bendita_sea'),
        sumados: contarReaccCabala(c.cabala_id, 'me_sumo'),
        miReaccion: miReaccionPorCabala(c.cabala_id),
        aprobacionPublica: c.visibilidad === 'publica' ? 'pendiente' : 'no_aplica',
    }));
    const activeCabalas = cabalas.filter(c => c.expiresAt > now);
    const userActiveCabalas = activeCabalas.filter(c => c.creadorId?.toString() === jugadorActivo.id?.toString() || c.creadorId?.toString() === jugadorActivo.user_id?.toString());
    const canCreateCabala = userActiveCabalas.length < 3;
    const isOwnCabala = (c) => c.creadorId?.toString() === jugadorActivo.id?.toString() || c.creadorId?.toString() === jugadorActivo.user_id?.toString();
    const cabalaReactionTotal = (c, reaction) => {
        if (reaction === 'bendita_sea') return c.bendiciones || 0;
        if (reaction === 'me_sumo') return c.sumados || 0;
        return 0;
    };
    const formatRemaining = (expiresAt) => {
        const diff = Math.max(0, expiresAt - Date.now());
        const hours = Math.ceil(diff / (60 * 60 * 1000));
        if (hours < 24) return `${hours} h restantes`;
        const days = Math.ceil(hours / 24);
        return `${days} días restantes`;
    };
    const visibilityMeta = {
        secreta: { label: 'Secreta', icon: '🔒', color: '#c4b5fd', note: 'solo el creador conoce el contenido' },
        grupo: { label: 'Privada grupo', icon: '🏠', color: 'var(--celeste)', note: 'visible dentro del grupo' },
        publica: { label: 'Pública', icon: '🌎', color: '#86efac', note: 'requiere aprobación para tribuna' }
    };

    // ── Promesas: lectura + adaptación. Estados DB → vocabulario UI.
    const promiseDeadlineOptions = [
        { label: 'Fin del partido', ms: 2 * 60 * 60 * 1000 },
        { label: '1 día', ms: 24 * 60 * 60 * 1000 },
        { label: '1 semana', ms: 7 * 24 * 60 * 60 * 1000 },
        { label: 'Fin del mundial', ms: 45 * 24 * 60 * 60 * 1000 }
    ];
    const promesasRaw = await getPromesasDelGrupo(groupId);
    const reaccionesPromesas = await getReaccionesPorRituales('promesa', promesasRaw.map(p => p.promesa_id));
    const contarReaccPromesa = (id, tipo) => reaccionesPromesas.filter(r => r.ritual_id === id && r.tipo_reaccion === tipo).length;
    // Doc Promesas v1.0 §6: 3 reacciones (Me sumo / Jua / Al pasto), 1 por
    // jugador. miReaccion marca la reacción "social" activa (no al_pasto);
    // miAlPasto marca si ya reporté (bloqueo simétrico a arengas v1.2 §9).
    const miReaccionPorPromesa = (promesaId) => {
        if (!jugadorActivo?.id) return null;
        const r = reaccionesPromesas.find(x => x.ritual_id === promesaId && x.player_id === jugadorActivo.id && x.tipo_reaccion !== 'al_pasto');
        return r?.tipo_reaccion || null;
    };
    const miAlPastoPorPromesa = (promesaId) => {
        if (!jugadorActivo?.id) return false;
        return reaccionesPromesas.some(x => x.ritual_id === promesaId && x.player_id === jugadorActivo.id && x.tipo_reaccion === 'al_pasto');
    };
    const estadoUI = { en_extension: 'extension', pendiente_validacion: 'validacion' };
    const apodoDe = (playerId) => (grupo.jugadores || []).find(j => j.id?.toString() === playerId?.toString())?.apodo || null;
    const promesas = promesasRaw.map(p => ({
        id: p.promesa_id,
        condicion: p.condicion,
        compromiso: p.compromiso,
        creadorId: p.creador_id,
        creador: p.jugadores?.display_name || 'Jugador',
        grupoOrigen: grupo.nombre,
        visibilidad: p.visibilidad === 'privada' ? 'grupo' : p.visibilidad,
        estado: estadoUI[p.estado] || p.estado,
        deadlineLabel: p.extension_label || 'Plazo',
        createdAt: new Date(p.created_at).getTime(),
        deadlineAt: new Date(p.deadline_at).getTime(),
        extendedAt: p.extended_at ? new Date(p.extended_at).getTime() : null,
        extensionLabel: p.extension_label || '',
        closedAt: p.closed_at ? new Date(p.closed_at).getTime() : null,
        validadoPor: apodoDe(p.validador_id),
        evidencia: p.evidencia || '',
        folkloreDelta: p.folklore_delta || 0,
        aprobacionPublica: p.visibilidad === 'publica' ? 'pendiente' : 'no_aplica',
        sumadosGrupo: contarReaccPromesa(p.promesa_id, 'sumo'), sumadosPublicos: 0,
        divertidosGrupo: contarReaccPromesa(p.promesa_id, 'divierte'), divertidosPublicos: 0,
        pasto: contarReaccPromesa(p.promesa_id, 'al_pasto'),
        miReaccion: miReaccionPorPromesa(p.promesa_id),
        miAlPasto: miAlPastoPorPromesa(p.promesa_id),
    }));
    const isOwnPromesa = (p) => p.creadorId?.toString() === jugadorActivo.id?.toString() || p.creadorId?.toString() === jugadorActivo.user_id?.toString();
    const isPromiseOpen = (p) => ['activa', 'extension', 'validacion'].includes(p.estado);
    const userOpenPromises = promesas.filter(p => isPromiseOpen(p) && (p.creadorId?.toString() === jugadorActivo.id?.toString() || p.creadorId?.toString() === jugadorActivo.user_id?.toString()));
    const canCreatePromesa = userOpenPromises.length < 3;
    const promesasAbiertas = promesas.filter(isPromiseOpen);
    const promesasPendientesResolucion = promesasAbiertas.filter(p => p.estado === 'validacion' || p.deadlineAt <= now);
    const promesasActivas = promesasAbiertas.filter(p => p.estado !== 'validacion' && p.deadlineAt > now);
    const promesasCerradas = promesas.filter(p => !isPromiseOpen(p));
    const promesaReactionTotal = (p, reaction) => {
        if (reaction === 'sumo') return (p.sumadosGrupo || 0) + (p.sumadosPublicos || 0);
        if (reaction === 'divierte') return (p.divertidosGrupo || 0) + (p.divertidosPublicos || 0);
        if (reaction === 'pasto') return p.pasto || 0;
        return 0;
    };
    const promiseStatusMeta = {
        activa: { icon: '🟡', label: 'Activa', color: '#fde68a' },
        extension: { icon: '🟠', label: 'Activa · Extensión', color: '#fdba74' },
        validacion: { icon: '🟢', label: 'Cumplida pendiente de validación', color: '#7dd3fc' },
        cumplida: { icon: '✅', label: 'Cumplida validada', color: '#86efac' },
        incumplida: { icon: '🔴', label: 'Incumplida', color: '#fca5a5' }
    };

    // ── Chat del grupo (panel colapsable nuevo — spec Chat v1.0 §1).
    const mensajes = await getMensajesDelGrupo(groupId);
    const renderMensajeChat = (m) => {
        const apodo = m.jugadores?.display_name || 'Jugador';
        const esExtranjero = (m.jugadores?.usuarios?.tipo) === 'extranjero';
        return `
            <div style="display:flex;flex-direction:column;gap:.1rem;">
                <span style="display:flex;align-items:center;gap:.3rem;font-size:.66rem;">
                    <strong style="color:${esExtranjero ? '#fbbf24' : 'var(--celeste)'};">${escHtml(apodo)}</strong>
                    ${esExtranjero ? '<span style="font-size:.58rem;background:rgba(251,191,36,.14);color:#fbbf24;border:1px solid rgba(251,191,36,.3);border-radius:999px;padding:.02rem .32rem;font-weight:900;">✈ Extranjero</span>' : ''}
                </span>
                <span style="color:var(--white);font-size:.82rem;line-height:1.25;overflow-wrap:anywhere;">${escHtml(m.contenido)}</span>
            </div>
        `;
    };
    const chatPanelHTML = `
        <details id="chatPanel" class="glass-card" style="margin-bottom:0.85rem;padding:0;border-color:rgba(117,170,219,0.2);background:linear-gradient(135deg,rgba(117,170,219,0.08) 0%,rgba(255,255,255,0.035) 100%);overflow:hidden;">
            <summary style="list-style:none;cursor:pointer;padding:0.72rem 0.85rem;display:flex;align-items:center;justify-content:space-between;gap:0.6rem;">
                <span style="display:flex;align-items:center;gap:0.5rem;min-width:0;">
                    <span style="font-size:1.05rem;">💬</span>
                    <strong style="color:var(--white);font-size:0.9rem;">Chat del grupo</strong>
                    <span style="color:var(--text-muted);font-size:0.7rem;">${mensajes.length} mensaje${mensajes.length === 1 ? '' : 's'}</span>
                </span>
                <span style="color:var(--text-muted);font-size:0.7rem;">desplegar</span>
            </summary>
            <div style="padding:0 0.85rem 0.85rem;">
                <div id="chatMensajes" style="display:flex;flex-direction:column;gap:0.4rem;max-height:240px;overflow-y:auto;padding:0.55rem;background:rgba(0,0,0,0.18);border-radius:10px;margin-bottom:0.55rem;">
                    ${mensajes.length ? mensajes.map(renderMensajeChat).join('') : '<span style="color:var(--text-muted);font-size:0.76rem;text-align:center;padding:0.6rem;">Todavía no hay mensajes. Arrancá el folklore.</span>'}
                </div>
                <form id="chatForm" style="display:flex;gap:0.4rem;">
                    <input id="chatInput" type="text" maxlength="500" autocomplete="off" placeholder="Escribí un mensaje…" style="flex:1;min-width:0;border-radius:999px;border:1px solid rgba(255,255,255,0.12);background:rgba(0,0,0,0.18);color:var(--white);padding:0.5rem 0.78rem;font-size:0.82rem;">
                    <button class="btn btn-primary" type="submit" style="width:auto;margin:0;padding:0.5rem 0.95rem;min-height:38px;background:linear-gradient(135deg,var(--celeste) 0%,#a855f7 100%);border:none;font-size:0.8rem;">Enviar</button>
                </form>
            </div>
        </details>
    `;

    const tabButton = (id, icon, label, count) => {
        const isActive = tab === id;
        const activeStyle = isActive
            ? 'background: linear-gradient(135deg, rgba(117,170,219,0.95) 0%, rgba(168,85,247,0.9) 100%); color: var(--white); border-color: rgba(255,255,255,0.26); box-shadow: 0 8px 18px rgba(117,170,219,0.18);'
            : 'background: rgba(255,255,255,0.055); color: var(--text-muted); border-color: var(--glass-border);';
        const flex = id === 'arengas' ? '1.28' : '1';

        return `
            <button class="btn" style="flex: ${flex}; min-width: 0; margin: 0; padding: 0.7rem 0.45rem; border-radius: 999px; border: 1px solid; ${activeStyle} display: flex; align-items: center; justify-content: center; gap: 0.28rem; font-size: 0.78rem; line-height: 1; white-space: nowrap;" onclick="window.location.hash='/rituales/${codigo}/${id}'">
                <span>${icon}</span>
                <span style="font-weight: 800;">${label}</span>
                <span style="font-size: 0.68rem; opacity: 0.8;">${count}</span>
            </button>
        `;
    };

    const renderArengaTypeBadge = (type, size = 'small') => {
        const meta = arengaTypes[type] || arengaTypes.vamos;
        const dimension = size === 'large' ? '68px' : size === 'medium' ? '46px' : '34px';
        const iconSize = size === 'large' ? 34 : size === 'medium' ? 24 : 18;
        return `<span title="${meta.label}" style="width: ${dimension}; height: ${dimension}; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(135deg, ${meta.color} 0%, rgba(255,255,255,0.14) 100%); border: 1px solid rgba(255,255,255,0.18); box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 18px ${meta.color}28;">${renderPactoIcon(meta.icon, '#ffffff', iconSize)}</span>`;
    };

    const renderArengaDetail = (a) => {
        if (!a) return '';
        const meta = arengaTypes[a.tipo] || arengaTypes.vamos;
        const mine = isOwnArenga(a);
        const hasAudio = Boolean(a.audioUrl);
        return `
            <div data-arenga-detail-open="1" style="position: fixed; inset: 0; z-index: 80; background: rgba(0,0,0,0.50); display: flex; align-items: flex-start; justify-content: center; padding: 10.2rem 0.72rem 0.72rem; padding: clamp(10.2rem, 30vh, 13rem) 0.72rem 0.72rem; overflow-y: auto;" onclick="if(event.target === this) window.location.hash='/rituales/${codigo}/arengas'">
                <div class="glass-card" style="width: min(100%, 420px); max-height: calc(100vh - 10.95rem); max-height: calc(100dvh - clamp(10.95rem, 31vh, 13.75rem)); overflow-y: auto; padding: 0.72rem; border-color: ${meta.color}55; background: radial-gradient(circle at 18% 0%, ${meta.color}24, transparent 34%), rgba(12,18,30,0.96); border-radius: 14px;">
                    <div style="display: flex; justify-content: space-between; gap: 0.65rem; align-items: flex-start; margin-bottom: 0.52rem;">
                        <div style="display: flex; gap: 0.58rem; align-items: center; min-width: 0;">
                            ${renderArengaTypeBadge(a.tipo, 'medium')}
                            <div style="min-width: 0;">
                                <span style="display: block; color: ${meta.color}; font-weight: 900; font-size: 0.66rem; text-transform: uppercase;">${meta.label}</span>
                                <h3 style="margin: 0.12rem 0 0; color: var(--white); font-family: var(--font-heading); font-size: 0.98rem; line-height: 1.02; overflow-wrap: anywhere;">${a.titulo || meta.label}</h3>
                            </div>
                        </div>
                        <button class="btn btn-secondary" style="width: auto; min-height: 30px; margin: 0; padding: 0.28rem 0.52rem; border-radius: 999px;" onclick="window.location.hash='/rituales/${codigo}/arengas'">X</button>
                    </div>

                    <div style="padding: 0.56rem; border-radius: 11px; background: rgba(255,255,255,0.052); border: 1px solid rgba(255,255,255,0.09);">
                        ${hasAudio ? `<audio id="arengaAudio" preload="metadata" src="${a.audioUrl}" style="display:none;"></audio>` : ''}
                        <div style="display:flex;align-items:center;gap:.6rem;">
                            <button id="arengaPlayBtn" class="btn btn-primary" ${hasAudio ? '' : 'disabled'} style="width: 42px; height: 42px; min-height: 42px; border-radius: 50%; margin: 0; padding: 0; display: flex; align-items: center; justify-content: center; font-size: 0.76rem; background: linear-gradient(135deg, ${meta.color} 0%, var(--celeste) 100%); border: none; ${hasAudio ? '' : 'opacity:.45;'}">Play</button>
                            <div style="flex:1;min-width:0;">
                                <div style="height: 6px; border-radius: 999px; overflow: hidden; background: rgba(255,255,255,0.12);">
                                    <div id="arengaProgress" style="width: 0%; height: 100%; background: linear-gradient(90deg, ${meta.color}, var(--celeste)); transition: width 0.25s linear;"></div>
                                </div>
                                <div style="display: flex; justify-content: space-between; color: var(--text-muted); font-size: 0.64rem; margin-top: 0.32rem;">
                                    <span>0:00</span>
                                    <span>${a.duracion}</span>
                                </div>
                            </div>
                        </div>
                        ${hasAudio ? '' : '<span style="display:block;color:var(--text-muted);font-size:.66rem;margin-top:.34rem;">Audio aún no subido.</span>'}
                    </div>

                    <div style="display: flex; flex-wrap: wrap; gap: 0.34rem; margin-top: 0.48rem; align-items: center;">
                        <span style="font-size: 0.7rem; color: var(--text-muted);">De ${a.creador}</span>
                        <span style="font-size: 0.7rem; color: var(--text-muted);">${a.grupoOrigen}</span>
                        ${renderVisibilityBadge(a.visibilidad)}
                    </div>
                    <!-- Spec arengas v1.2 §5: tiempo obligatorio en despliegue.
                         En linea propia debajo del creador+grupo para que no quede mezclado. -->
                    <div style="margin-top:.32rem;display:flex;justify-content:space-between;gap:.5rem;font-size:.7rem;">
                        <span style="color:var(--text-muted);">${arengaCreatedAgo(a.createdAt)}</span>
                        ${(() => { const t = arengaTotalReactions(a); const p = puntosPorRitual({ reaccionesTotal: t }); return `<span data-rit-pts="arenga:${a.id}" data-rit-reac="${t}" style="color:#7ab488;">Puntos: ${p>0?'+':''}${p}</span>`; })()}
                    </div>

                    ${mine ? `
                        <div style="margin-top:.58rem;padding:.36rem .5rem;border-radius:11px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.66rem;text-align:center;">Es tu arenga — no podés reaccionar a vos mismo.</div>
                        <!-- Decisión 28-may del dueño: el creador ve los 4 contadores
                             reales de su arenga (3 sociales + Al pasto), apagados.
                             Reemplaza la regla histórica del doc v1.2 §169 que ocultaba
                             el contador de Al pasto al creador. -->
                        <div style="display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.3rem; margin-top: 0.4rem;">
                            ${reactionDefs.map(reaction => `
                                <div aria-disabled="true" style="margin:0; min-height:36px; padding:0.26rem 0.2rem; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:10px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.1rem; opacity:.55; cursor:default; user-select:none;">
                                    <span style="display:flex;align-items:center;gap:.12rem;color:var(--white);font-size:0.58rem;line-height:1;white-space:nowrap;">${renderPactoIcon(reaction.icon, reaction.color, 11)} ${reaction.label}</span>
                                    <span style="color:${reaction.color};font-size:0.64rem;font-weight:900;">${arengaReactionTotal(a, reaction.id)}</span>
                                </div>
                            `).join('')}
                        </div>
                    ` : `
                        <div style="display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.3rem; margin-top: 0.58rem;">
                            ${reactionDefs.filter(reaction => reaction.id !== 'pasto').map(reaction => {
                                const active = a.miReaccion === reaction.id;
                                const bg = active ? `${reaction.color}29` : (reaction.id === 'escuchar' ? 'rgba(117,170,219,0.12)' : 'rgba(255,255,255,0.055)');
                                const border = active ? `${reaction.color}cc` : (reaction.id === 'escuchar' ? 'rgba(117,170,219,0.3)' : 'rgba(255,255,255,0.1)');
                                return `
                                    <button class="btn btn-secondary arenga-react${active ? ' arenga-react-active' : ''}" data-id="${a.id}" data-reaction="${reaction.id}" data-active="${active ? '1' : '0'}" style="margin:0; min-height:36px; padding:0.26rem 0.2rem; background:${bg}; border-color:${border}; ${active ? 'box-shadow: 0 0 0 1px ' + reaction.color + '55 inset;' : ''} display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.1rem;">
                                        <span style="display:flex;align-items:center;gap:.12rem;color:var(--white);font-size:0.58rem;line-height:1;white-space:nowrap;">${renderPactoIcon(reaction.icon, reaction.color, 11)} ${reaction.label}</span>
                                        <span style="color:${reaction.color};font-size:0.64rem;font-weight:900;">${arengaReactionTotal(a, reaction.id)}</span>
                                    </button>
                                `;
                            }).join('')}
                        </div>
                    `}

                    <div style="display:grid;grid-template-columns:${mine ? '1fr' : '3fr 1fr'};gap:.38rem;margin-top:.42rem;">
                        <button class="btn btn-secondary arenga-save" data-id="${a.id}" data-saved="${isArengaSaved(a.id) ? '1' : '0'}" style="margin:0; min-height:34px; padding:0.4rem 0.55rem; background:${isArengaSaved(a.id) ? 'rgba(34,197,94,.14)' : 'rgba(255,255,255,0.055)'}; border-color:${isArengaSaved(a.id) ? 'rgba(34,197,94,.32)' : 'rgba(255,255,255,0.12)'}; color:${isArengaSaved(a.id) ? '#bbf7d0' : 'var(--white)'}; font-size:0.7rem; font-weight:${isArengaSaved(a.id) ? '900' : '600'};">
                            ${isArengaSaved(a.id) ? '✓ Guardada' : 'Guardar arenga'}
                        </button>
                        ${mine ? '' : `<button class="btn btn-secondary arenga-react${a.miAlPasto ? ' arenga-react-active' : ''}" data-id="${a.id}" data-reaction="pasto" data-active="${a.miAlPasto ? '1' : '0'}" style="margin:0; min-height:34px; padding:0.38rem 0.22rem; background:rgba(192,132,252,${a.miAlPasto ? '0.22' : '0.08'}); border-color:rgba(192,132,252,${a.miAlPasto ? '0.6' : '0.18'}); color:#ddd6fe; font-size:0.62rem; display:flex; align-items:center; justify-content:center; gap:.28rem; ${a.miAlPasto ? 'box-shadow:0 0 0 1px rgba(192,132,252,.35) inset;' : ''}"><span>${a.miAlPasto ? '✓ Reportado' : '🥾 Al pasto'}</span><span style="color:#c084fc;font-weight:900;">${arengaReactionTotal(a, 'pasto')}</span></button>`}
                    </div>

                    ${a.visibilidad === 'publica' && a.audioId ? `
                        <button class="btn btn-secondary arenga-share" data-audio-id="${a.audioId}" data-titulo="${(a.titulo || 'arenga mundialista').replace(/"/g, '&quot;')}" data-creador="${a.creador.replace(/"/g, '&quot;')}" style="width:100%;margin:.42rem 0 0;min-height:38px;padding:.48rem .62rem;border-radius:999px;background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.18);color:#bbf7d0;font-size:.74rem;font-weight:900;">🔗 Compartir</button>
                    ` : ''}

                    ${mine ? `
                        <div style="margin-top:.42rem;">
                            <button class="btn btn-secondary arenga-eliminar" data-id="${a.id}" style="margin:0;width:100%;min-height:38px;padding:.48rem .42rem;border-radius:999px;background:rgba(248,113,113,.08);border:1px solid rgba(248,113,113,.22);color:#fecaca;font-size:.7rem;font-weight:900;">🗑️ Eliminar</button>
                        </div>
                    ` : ''}
                </div>
            </div>
        `;
    };

    const renderCrearArengaSheet = () => {
        if (!isCreatingArenga) return '';
        // Spec arengas v1.2 §2: solo iteramos los 8 tipos canónicos del base
        // (no las claves del Proxy, que aceptaría cualquier key desconocida).
        const typeEntries = Object.entries(arengaTypesBase);
        const shortLabels = {
            vamos: 'Vamos',
            opinion: 'Opinión',
            sufrimiento: 'Sufrimiento',
            humor: 'Humor',
            festejo: 'Festejo',
            canticos: 'Cánticos',
            bronca: 'Bronca',
            declaracion: 'Declaración'
        };
        return `
            <div id="arengaCreateOverlay" style="position: fixed; inset: 0; z-index: 110; background: rgba(0,0,0,0.58); display: flex; align-items: flex-end; justify-content: center; padding: 0.85rem;">
                <div class="glass-card" style="width:min(100%,430px);max-height:85%;min-height:0;box-sizing:border-box;overflow-y:scroll;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding:1rem 1rem 6rem;border-radius:16px 16px 12px 12px;border-color:rgba(117,170,219,.24);background:radial-gradient(circle at 18% 0%,rgba(117,170,219,.18),transparent 34%),rgba(12,18,30,.97);">
                    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:.8rem;margin-bottom:.85rem;">
                        <div>
                            <h3 style="margin:0;color:var(--white);font-family:var(--font-heading);font-size:1.14rem;">Crear arenga</h3>
                            <span style="display:block;color:var(--text-muted);font-size:.78rem;margin-top:.18rem;">Grabá tu emoción para el grupo.</span>
                        </div>
                        <button type="button" class="btn btn-secondary" data-arenga-close="1" style="width:auto;min-height:34px;margin:0;padding:.35rem .6rem;border-radius:999px;">X</button>
                    </div>

                    <div style="display:grid;gap:.55rem;">
                        <strong style="color:var(--white);font-size:.82rem;">Tipo de arenga</strong>
                        <div style="display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.42rem;">
                            ${typeEntries.map(([type, meta], index) => `
                                <button type="button" class="btn btn-secondary arenga-create-type ${index === 0 ? 'active' : ''}" data-type="${type}" style="margin:0;min-height:70px;padding:.48rem .28rem;display:flex;flex-direction:column;gap:.28rem;background:${index === 0 ? `${meta.color}1f` : 'rgba(255,255,255,.045)'};border-color:${index === 0 ? `${meta.color}80` : 'rgba(255,255,255,.1)'};">
                                    <span style="width:30px;height:30px;border-radius:11px;display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(135deg,${meta.color},rgba(255,255,255,.14));">${renderPactoIcon(meta.icon, '#fff', 16)}</span>
                                    <span style="font-size:.62rem;line-height:1.05;color:var(--white);font-weight:800;">${shortLabels[type] || meta.label}</span>
                                </button>
                            `).join('')}
                        </div>
                    </div>

                    <div style="display:grid;gap:.55rem;margin-top:.95rem;">
                        <strong style="color:var(--white);font-size:.82rem;">Título (opcional)</strong>
                        <input id="arengaTituloInput" type="text" maxlength="20" placeholder="Máx 20 caracteres — usa el nombre del tipo si lo dejás vacío" style="width:100%;padding:.55rem .7rem;border:1px solid rgba(255,255,255,.12);border-radius:9px;background:rgba(255,255,255,.045);color:var(--white);font-size:.82rem;">
                    </div>

                    <div style="display:grid;gap:.55rem;margin-top:.95rem;">
                        <strong style="color:var(--white);font-size:.82rem;">Visibilidad</strong>
                        <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem;">
                            <button type="button" class="btn btn-secondary arenga-create-visibility active" data-visibility="grupo" style="margin:0;min-height:62px;padding:.6rem;background:rgba(117,170,219,.12);border-color:rgba(117,170,219,.32);display:flex;flex-direction:column;gap:.18rem;">
                                <span style="color:var(--white);font-size:.82rem;">Privada</span>
                                <span style="color:var(--text-muted);font-size:.66rem;">Solo visible para tu grupo</span>
                            </button>
                            <button type="button" class="btn btn-secondary arenga-create-visibility" data-visibility="publica" style="margin:0;min-height:78px;padding:.6rem;background:rgba(255,255,255,.045);border-color:rgba(255,255,255,.1);display:flex;flex-direction:column;gap:.18rem;">
                                <span style="color:var(--white);font-size:.82rem;">Pública</span>
                                <span style="color:var(--text-muted);font-size:.66rem;">Puede ir a la Tribuna pública</span>
                                <span style="color:var(--text-muted);font-size:.62rem;font-weight:400;opacity:.7;font-style:italic;">Autorizo uso en video</span>
                            </button>
                        </div>
                        <span style="color:var(--text-muted);font-size:.68rem;line-height:1.25;">Las arengas públicas pueden ser destacadas en la tribuna nacional.</span>
                        <span class="arenga-consentimiento-publico" style="color:var(--text-muted);font-size:.64rem;line-height:1.3;opacity:.85;">Al hacerla pública, tu arenga puede aparecer en la Tribuna y en otros canales de Pacto Mundial.</span>
                        <div class="arenga-tribuna-libre-cartel" style="display:none;margin-top:.2rem;padding:.6rem .72rem;border-radius:11px;background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.22);color:#fcd34d;font-size:.68rem;line-height:1.35;">⚠️ Hasta el debut de Argentina podés subir a la Tribuna pública gratis. Después vas a necesitar el pacto certificado. Certificá el tuyo y asegurate tu lugar de Fundador.</div>
                    </div>

                    <div style="margin-top:.95rem;padding:.85rem;border-radius:13px;background:rgba(255,255,255,.052);border:1px solid rgba(255,255,255,.09);">
                        <div style="display:flex;align-items:center;justify-content:space-between;gap:.7rem;margin-bottom:.65rem;">
                            <strong style="color:var(--white);font-size:.82rem;">Grabación</strong>
                            <span style="color:var(--text-muted);font-size:.68rem;">Máximo 20 segundos</span>
                        </div>
                        <button type="button" class="btn btn-primary" id="arengaRecordBtn" style="margin:0;min-height:58px;background:linear-gradient(135deg,var(--celeste),#f59e0b);border:none;">Grabar arenga</button>
                        <div style="margin-top:.65rem;height:7px;border-radius:999px;background:rgba(255,255,255,.12);overflow:hidden;">
                            <div id="arengaRecordProgress" style="width:0%;height:100%;background:linear-gradient(90deg,var(--celeste),#f59e0b);transition:width .2s linear;"></div>
                        </div>
                        <div style="display:flex;align-items:center;justify-content:space-between;margin-top:.42rem;color:var(--text-muted);font-size:.72rem;">
                            <span id="arengaRecordTimer">00:00 / 00:20</span>
                            <span id="arengaRecordStatus">Listo para grabar</span>
                        </div>
                        <div id="arengaPreviewActions" style="display:none;grid-template-columns:1fr 1fr;gap:.5rem;margin-top:.75rem;">
                            <button type="button" class="btn btn-secondary" id="arengaRetryBtn" style="margin:0;min-height:42px;padding:.58rem;">Volver a grabar</button>
                            <button type="button" class="btn btn-secondary" id="arengaPreviewBtn" style="margin:0;min-height:42px;padding:.58rem;">Preview</button>
                        </div>
                    </div>

                    <button type="button" class="btn btn-primary" id="arengaPublishBtn" disabled style="margin:.8rem 0 0;min-height:50px;background:linear-gradient(135deg,var(--celeste),#22c55e);border:none;opacity:.45;">Publicar arenga</button>
                </div>
            </div>
        `;
    };

    const cabalaStatusLine = (c, meta) => {
        if (c.visibilidad === 'secreta') return `🔒 CÁBALA SECRETA · ⏳ ${formatRemaining(c.expiresAt)}`;
        if (c.visibilidad === 'publica') return `🌎 PÚBLICA · ⏳ ${formatRemaining(c.expiresAt)}`;
        return `🟢 CÁBALA ACTIVA · ⏳ ${formatRemaining(c.expiresAt)}`;
    };
    const cabalaSecondaryNote = (c, meta) => {
        if (c.visibilidad === 'secreta') return '🔒 Solo el creador conoce el contenido';
        if (c.visibilidad === 'publica' && c.aprobacionPublica === 'pendiente') return '🟡 Pendiente de moderación para Tribuna Pública';
        if (c.visibilidad === 'publica') return '🌎 Visible en Tribuna Pública';
        return '👥 Visible dentro del grupo';
    };

    const arengasHTML = `
        <div style="display: flex; justify-content: space-between; gap: 0.8rem; align-items: center; margin-bottom: 0.85rem;">
            <div>
                <strong style="display:block;color:var(--white);font-size:0.95rem;">Radio emocional del grupo</strong>
                <span style="display:block;color:var(--text-muted);font-size:0.74rem;margin-top:0.2rem;">Tocá una card para escuchar sin armar caos sonoro.</span>
            </div>
            <button class="btn btn-primary" id="btnMockArenga" style="width:auto;margin:0;padding:0.62rem 0.75rem;border:none;background:linear-gradient(135deg,var(--celeste) 0%,#f59e0b 100%);font-size:0.76rem;">+ Crear arenga</button>
        </div>
        <div style="display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.48rem; margin-bottom: 2rem;">
            ${[...arengas].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map(a => {
                const meta = arengaTypes[a.tipo] || arengaTypes.vamos;
                const mine = isOwnArenga(a);
                return `
                    <button class="glass-card arenga-open" data-id="${a.id}" style="aspect-ratio: 1; padding: 0.55rem; margin: 0; border: 1px solid ${mine ? 'rgba(117,170,219,.5)' : `${meta.color}33`}; background: radial-gradient(circle at 20% 0%, ${meta.color}30, transparent 42%), rgba(255,255,255,0.045); color: var(--white); display: flex; flex-direction: column; align-items: flex-start; justify-content: space-between; text-align: left; cursor: pointer; border-radius: 10px; min-width: 0; box-shadow: 0 0 14px ${mine ? 'rgba(117,170,219,.18)' : `${meta.color}12`};">
                        <span style="width: 100%; display: flex; justify-content: space-between; align-items: flex-start; gap: 0.25rem;">
                            ${renderArengaTypeBadge(a.tipo)}
                            <span style="width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,0.12); display: inline-flex; align-items: center; justify-content: center; font-size: 0.72rem;">▶</span>
                        </span>
                        <span style="display: flex; flex-direction: column; gap: 0.16rem; width: 100%; min-width: 0;">
                            <strong style="font-size: 0.68rem; line-height: 1.05; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%;">${a.titulo || meta.label}</strong>
                            <span style="display:flex;align-items:center;gap:.28rem;min-width:0;font-size:0.62rem;color:var(--text-muted);overflow:hidden;white-space:nowrap;">
                                ${mine ? `<span style="flex:0 0 auto;color:var(--celeste);background:rgba(117,170,219,.11);border:1px solid rgba(117,170,219,.22);border-radius:999px;padding:.08rem .28rem;font-weight:900;">👤 Tuya</span>` : ''}
                                <span style="min-width:0;overflow:hidden;text-overflow:ellipsis;">${a.creador}</span>
                            </span>
                            <!-- Spec arengas v1.2 §5: tiempo obligatorio en card cerrada (linea propia para que no lo trunque el ellipsis del creador). -->
                            <span style="font-size:.6rem;color:var(--text-muted);white-space:nowrap;">${arengaCreatedAgo(a.createdAt)}</span>
                            <span style="font-size:.66rem;color:${meta.color};font-weight:900;">${arengaTotalReactions(a)} reacciones</span>
                            ${(() => { const t = arengaTotalReactions(a); const p = puntosPorRitual({ reaccionesTotal: t }); return `<span data-rit-pts="arenga:${a.id}" data-rit-reac="${t}" style="font-size:.6rem;color:#7ab488;">Puntos: ${p>0?'+':''}${p}</span>`; })()}
                        </span>
                    </button>
                `;
            }).join('')}
        </div>
        ${renderArengaDetail(selectedArenga)}
        ${renderCrearArengaSheet()}
    `;

    const cabalasHTML = `
        <div style="margin-bottom: 0.78rem;">
            <div class="glass-card" style="padding: 0.78rem; border-color: rgba(168,85,247,0.2); background: linear-gradient(135deg, rgba(168,85,247,0.1) 0%, rgba(255,255,255,0.035) 100%);">
                <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.58rem;">
                    <div>
                        <strong style="display: block; color: var(--white); font-size: 0.94rem;">🔮 Nueva cábala</strong>
                        <span style="display: block; color: var(--text-muted); font-size: 0.7rem; line-height: 1.25; margin-top: 0.18rem;">Se activa al crearla. No se edita ni se comparte afuera.</span>
                    </div>
                    <span style="flex: 0 0 auto; color: ${canCreateCabala ? '#86efac' : '#fca5a5'}; font-size: 0.68rem; font-weight: 900; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; padding: 0.22rem 0.46rem;">${userActiveCabalas.length}/3 activas</span>
                </div>
                ${canCreateCabala ? `
                    <form id="cabalaForm" style="display: flex; flex-direction: column; gap: 0.55rem;">
                        <textarea id="cabalaText" maxlength="120" required placeholder="Ej: No me saco esta camiseta hasta la final." style="width: 100%; min-height: 58px; resize: vertical; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.64rem 0.72rem; font-family: var(--font-body); font-size: 0.84rem; line-height: 1.25;"></textarea>
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;">
                            <select id="cabalaDuration" style="min-width: 0; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.58rem 0.62rem; font-size: 0.78rem;">
                                ${durationOptions.map(opt => `<option value="${opt.ms}|${opt.label}">${opt.label}</option>`).join('')}
                            </select>
                            <select id="cabalaVisibility" style="min-width: 0; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.58rem 0.62rem; font-size: 0.78rem;">
                                <option value="grupo">🏠 Privada grupo</option>
                                <option value="secreta">🔒 Secreta</option>
                                <option value="publica">🌎 Pública</option>
                            </select>
                        </div>
                        <button class="btn btn-primary" type="submit" style="margin: 0; padding: 0.68rem; min-height: 42px; background: linear-gradient(135deg, #7c3aed 0%, #75aadb 100%); border: none;">Activar cábala · +1 folklore</button>
                    </form>
                ` : `
                    <div style="padding: 0.68rem; border-radius: 10px; background: rgba(252,165,165,0.08); border: 1px solid rgba(252,165,165,0.18); color: #fecaca; font-size: 0.78rem; line-height: 1.25;">Ya tenés 3 cábalas activadas. Cuando una expire, podés activar otra.</div>
                `}
            </div>
        </div>

        <div style="display: flex; flex-direction: column; gap: 0.62rem; margin-bottom: 2rem;">
            ${activeCabalas.map(c => {
                const meta = visibilityMeta[c.visibilidad] || visibilityMeta.grupo;
                const isSecret = c.visibilidad === 'secreta';
                return `
                    <div class="glass-card" style="padding: 0.78rem; background: radial-gradient(circle at 12% 0%, rgba(168,85,247,0.14), transparent 34%), rgba(255,255,255,0.04); border-color: rgba(168,85,247,0.18);">
                        <div style="display: flex; align-items: center; min-width: 0; margin-bottom: 0.42rem;">
                            <span style="display: inline-flex; align-items: center; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: ${meta.color}; background: rgba(255,255,255,0.045); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; padding: 0.22rem 0.5rem; font-weight: 900; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.25px;">
                                ${cabalaStatusLine(c, meta)}
                            </span>
                        </div>

                        ${isSecret && !isOwnCabala(c) ? `
                            <div style="padding: 0.56rem 0.62rem; border-radius: 10px; background: rgba(0,0,0,0.14); border: 1px dashed rgba(196,181,253,0.28); color: rgba(255,255,255,0.82); font-size: 0.86rem; line-height: 1.22; font-weight: 800; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                                ${c.creador} tiene una cábala secreta activa.
                            </div>
                        ` : `
                            ${isSecret && isOwnCabala(c) ? `<div style="display:flex; align-items:center; gap:.3rem; margin-bottom:.34rem; color:var(--text-muted); font-size:.62rem; font-weight:800;">👁️ Solo vos ves esto · al resto le aparece oculto</div>` : ''}
                            <blockquote style="margin: 0; color: var(--white); font-size: 0.94rem; line-height: 1.2; font-weight: 850; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">“${c.texto}”</blockquote>
                        `}

                        <div style="display: flex; align-items: center; gap: 0.3rem; margin-top: 0.46rem; color: var(--text-muted); font-size: 0.68rem; white-space: nowrap; overflow: hidden;">
                            <span style="min-width:0; overflow:hidden; text-overflow:ellipsis;">👤 ${c.creador} · 🏠 ${c.grupoOrigen} · ${meta.icon} ${meta.label}</span>
                        </div>

                        ${(() => {
                            // Doc Cábalas v1.0 §5 / patrón arengas: el creador ve los
                            // 2 contadores apagados (no reacciona a lo propio).
                            // No-creador: botones activos con marker de su reacción
                            // si ya eligió una. 'bendita_sea'/'me_sumo' = slug interno.
                            const mineC = isOwnCabala(c);
                            const reacts = [
                                { id: 'bendita_sea', label: '🙏 Bendita sea', color: '#c084fc' },
                                { id: 'me_sumo',     label: '✋ Me sumo',     color: '#fbbf24' },
                            ];
                            if (mineC) {
                                return `
                                <div style="margin-top:.5rem;padding:.32rem .5rem;border-radius:10px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.62rem;text-align:center;">Es tu cábala — no podés reaccionar a vos mismo.</div>
                                <div style="display:grid;grid-template-columns:1fr 1fr;gap:.3rem;margin-top:.34rem;">
                                    ${reacts.map(reaction => `
                                        <div aria-disabled="true" style="margin:0;min-height:34px;padding:.32rem .42rem;border-radius:999px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);display:flex;align-items:center;justify-content:center;gap:.3rem;opacity:.55;cursor:default;user-select:none;">
                                            <span style="font-size:.72rem;color:var(--white);white-space:nowrap;">${reaction.label}</span>
                                            <span style="color:${reaction.color};font-size:.66rem;font-weight:900;">${cabalaReactionTotal(c, reaction.id)}</span>
                                        </div>
                                    `).join('')}
                                </div>`;
                            }
                            return `
                            <div style="display:grid;grid-template-columns:1fr 1fr;gap:.3rem;margin-top:.55rem;">
                                ${reacts.map(reaction => {
                                    const active = c.miReaccion === reaction.id;
                                    const bg = active ? `${reaction.color}29` : 'rgba(255,255,255,0.055)';
                                    const border = active ? `${reaction.color}cc` : 'rgba(255,255,255,0.1)';
                                    return `<button class="btn btn-secondary cabala-react${active ? ' cabala-react-active' : ''}" data-id="${c.id}" data-reaction="${reaction.id}" data-active="${active ? '1' : '0'}" style="margin:0;min-height:36px;padding:.42rem .48rem;border-radius:999px;background:${bg};border-color:${border};${active ? 'box-shadow:0 0 0 1px ' + reaction.color + '55 inset;' : ''}display:flex;align-items:center;justify-content:center;gap:.34rem;">
                                        <span style="font-size:.76rem;color:var(--white);white-space:nowrap;">${reaction.label}</span>
                                        <span style="color:${reaction.color};font-size:.68rem;font-weight:900;">${cabalaReactionTotal(c, reaction.id)}</span>
                                    </button>`;
                                }).join('')}
                            </div>`;
                        })()}

                        <div style="display: flex; justify-content: space-between; gap: 0.5rem; margin-top: 0.42rem; color: var(--text-muted); font-size: 0.62rem; line-height: 1.15; opacity: 0.78; white-space: nowrap; overflow: hidden;">
                            ${(() => { const t = (c.bendiciones||0)+(c.sumados||0); const p = puntosPorRitual({ reaccionesTotal: t }); return `<span data-rit-pts="cabala:${c.id}" data-rit-reac="${t}" style="color:#7ab488;">Puntos: ${p>0?'+':''}${p}</span>`; })()}
                            <span style="min-width:0;overflow:hidden;text-overflow:ellipsis;">${cabalaSecondaryNote(c, meta)}</span>
                        </div>
                    </div>
                `;
            }).join('')}
        </div>
    `;

    const getPromiseCardTone = (estado) => {
        if (estado === 'cumplida') return 'rgba(34,197,94,0.08); border-color: rgba(34,197,94,0.22);';
        if (estado === 'validacion') return 'rgba(14,165,233,0.08); border-color: rgba(125,211,252,0.24);';
        if (estado === 'incumplida') return 'rgba(127,29,29,0.16); border-color: rgba(248,113,113,0.22);';
        return 'radial-gradient(circle at 12% 0%, rgba(245,158,11,0.13), transparent 34%), rgba(255,255,255,0.04); border-color: rgba(245,158,11,0.2);';
    };

    const renderPromesaCard = (p, options = {}) => {
        const meta = promiseStatusMeta[p.estado] || promiseStatusMeta.activa;
        const vis = visibilityMeta[p.visibilidad] || visibilityMeta.grupo;
        const isSecret = p.visibilidad === 'secreta';
        const isCreator = p.creadorId?.toString() === jugadorActivo.id?.toString() || p.creadorId?.toString() === jugadorActivo.user_id?.toString();
        const isPublicPending = p.visibilidad === 'publica' && p.aprobacionPublica === 'pendiente';
        const canExtendPromise = isCreator && p.estado === 'activa' && !p.extendedAt;
        const canResolvePromise = isCreator && ['activa', 'extension'].includes(p.estado);

        return `
            <div class="glass-card promesa-card" data-promise-card="${p.id}" style="padding: 0.78rem; background: ${getPromiseCardTone(p.estado)}">
                <button type="button" class="promesa-card-header" data-promesa-toggle="${p.id}" aria-expanded="false" style="background:transparent;border:none;padding:0;margin:0;width:100%;font:inherit;color:inherit;text-align:left;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:0.6rem;">
                    <span style="flex:1;min-width:0;">
                        <strong style="display:block;color:var(--white);font-size:0.9rem;line-height:1.16;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${isSecret && isPromiseOpen(p) && !isCreator ? 'Promesa secreta' : p.compromiso}</strong>
                        <span style="display:block;color:var(--text-muted);font-size:0.66rem;margin-top:0.12rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">👤 ${p.creador}</span>
                    </span>
                    <span style="flex:0 0 auto;display:flex;align-items:center;gap:0.4rem;">
                        <span style="display:inline-flex;align-items:center;gap:0.2rem;color:${meta.color};background:rgba(255,255,255,0.045);border:1px solid rgba(255,255,255,0.08);border-radius:999px;padding:0.2rem 0.46rem;font-weight:900;font-size:0.62rem;text-transform:uppercase;white-space:nowrap;">${isSecret && isPromiseOpen(p) ? '🔒 Secreta' : `${meta.icon} ${meta.label}`}</span>
                        <span class="promesa-chevron" aria-hidden="true" style="color:var(--text-muted);font-size:0.8rem;display:inline-block;transition:transform 0.2s ease;">&#9662;</span>
                    </span>
                </button>
                <div class="promesa-card-body" data-promesa-body="${p.id}" style="display:none;margin-top:0.55rem;">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.55rem; margin-bottom: 0.48rem;">
                    <span style="display: inline-flex; align-items: center; gap: 0.25rem; min-width: 0; color: ${meta.color}; background: rgba(255,255,255,0.045); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; padding: 0.22rem 0.5rem; font-weight: 900; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.22px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        ${isSecret && isPromiseOpen(p) ? '🔒 Activa secreta' : `${meta.icon} ${meta.label}`}
                    </span>
                    <span style="font-size: 0.64rem; color: var(--text-muted); white-space: nowrap;">
                        ${isPromiseOpen(p) ? `⏳ ${formatRemaining(p.deadlineAt)}` : p.deadlineLabel}
                    </span>
                </div>

                ${isSecret && isPromiseOpen(p) && !isCreator ? `
                    <div style="padding: 0.62rem; border-radius: 10px; background: rgba(0,0,0,0.14); border: 1px dashed rgba(196,181,253,0.3); color: rgba(255,255,255,0.82); font-size: 0.86rem; line-height: 1.22; font-weight: 800;">
                        ${p.creador} tiene una promesa secreta activa.
                    </div>
                ` : `
                    ${isSecret && isCreator && isPromiseOpen(p) ? `<div style="display:flex; align-items:center; gap:.3rem; margin-bottom:.4rem; color:var(--text-muted); font-size:.62rem; font-weight:800;">👁️ Solo vos ves esto · al resto le aparece oculto</div>` : ''}
                    <div style="display: grid; gap: 0.42rem;">
                        <div style="padding: 0.58rem 0.62rem; border-radius: 10px; background: rgba(0,0,0,0.14); border: 1px solid rgba(255,255,255,0.08);">
                            <span style="display: block; color: var(--text-muted); font-size: 0.62rem; text-transform: uppercase; font-weight: 900;">Si sucede...</span>
                            <strong style="display: block; color: var(--white); font-size: 0.92rem; line-height: 1.16; margin-top: 0.14rem; display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${p.condicion}</strong>
                        </div>
                        <div style="padding: 0.58rem 0.62rem; border-radius: 10px; background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.16);">
                            <span style="display: block; color: #fcd34d; font-size: 0.62rem; text-transform: uppercase; font-weight: 900;">Me comprometo a...</span>
                            <strong style="display: block; color: var(--white); font-size: 0.92rem; line-height: 1.16; margin-top: 0.14rem; display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${p.compromiso}</strong>
                        </div>
                    </div>
                `}

                <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.3rem; margin-top: 0.48rem; color: var(--text-muted); font-size: 0.68rem; overflow: hidden;">
                    <span style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">👤 ${p.creador} · 🏠 ${p.grupoOrigen} · ${vis.icon} ${vis.label}</span>
                    ${(() => { const t = (p.sumadosGrupo||0)+(p.divertidosGrupo||0)+(p.pasto||0); const pts = puntosPorRitual({ reaccionesTotal: t, esIncumplida: p.estado === 'incumplida' }); return `<span data-rit-pts="promesa:${p.id}" data-rit-reac="${t}" data-rit-incumplida="${p.estado === 'incumplida' ? '1' : '0'}" style="flex:0 0 auto;color:${pts>=0?'#7ab488':'#c47171'};font-size:.66rem;white-space:nowrap;">Puntos: ${pts>0?'+':''}${pts}</span>`; })()}
                </div>

                ${isPublicPending ? `<div style="margin-top: 0.42rem; color: #fde68a; background: rgba(245,158,11,0.07); border: 1px solid rgba(245,158,11,0.15); border-radius: 999px; padding: 0.28rem 0.52rem; font-size: 0.64rem; white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">🟡 Pendiente de moderación para Tribuna Pública</div>` : ''}
                ${p.extendedAt ? `<div style="margin-top: 0.42rem; color: #fed7aa; background: rgba(249,115,22,0.08); border: 1px solid rgba(249,115,22,0.16); border-radius: 999px; padding: 0.28rem 0.52rem; font-size: 0.64rem; white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">🟠 Extensión usada${p.extensionLabel ? ` · ${p.extensionLabel}` : ''}</div>` : ''}

                ${p.estado === 'cumplida' ? `<div style="margin-top: 0.42rem; padding: 0.42rem 0.55rem; border-radius: 999px; background: rgba(34,197,94,0.08); color: #bbf7d0; font-size: 0.66rem; white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">✅ Validada por ${p.validadoPor || 'el grupo'} · +20 folklore</div>` : ''}
                ${p.estado === 'validacion' ? `<div style="margin-top: 0.42rem; padding: 0.42rem 0.55rem; border-radius: 999px; background: rgba(14,165,233,0.08); color: #bae6fd; font-size: 0.66rem; white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">🟢 Cumplida: falta validación de otro integrante</div>` : ''}
                ${p.estado === 'incumplida' ? `<div style="margin-top: 0.42rem; padding: 0.42rem 0.55rem; border-radius: 999px; background: rgba(239,68,68,0.08); color: #fecaca; font-size: 0.66rem; white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">🔴 Incumplida · -100 folklore</div>` : ''}
                ${isPromiseOpen(p) ? (() => {
                    // Mismo patrón que arengas v1.2: creador ve 3 contadores
                    // apagados; no-creador interactúa con marker activo y
                    // bloqueo Al pasto si ya reportó (1 por jugador §9/§15).
                    const mineP = isOwnPromesa(p);
                    const reacts = [
                        { id: 'sumo',     label: '🔥 Me sumo', color: '#f87171' },
                        { id: 'divierte', label: '😂 Jua',      color: '#fbbf24' },
                    ];
                    if (mineP) {
                        const cells = [
                            ...reacts.map(r => `<div aria-disabled="true" style="margin:0;min-height:36px;padding:.42rem .26rem;border-radius:999px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);display:flex;align-items:center;justify-content:center;gap:.22rem;opacity:.55;cursor:default;user-select:none;"><span style="font-size:.72rem;color:var(--white);white-space:nowrap;">${r.label}</span><span style="color:${r.color};font-size:.64rem;font-weight:900;">${promesaReactionTotal(p, r.id)}</span></div>`),
                            `<div aria-disabled="true" style="margin:0;min-height:36px;padding:.42rem .26rem;border-radius:999px;background:rgba(192,132,252,.05);border:1px solid rgba(192,132,252,.14);display:flex;align-items:center;justify-content:center;gap:.18rem;opacity:.55;cursor:default;user-select:none;font-size:.7rem;color:#ddd6fe;">🥾 Al pasto <span style="color:#c084fc;font-size:.64rem;font-weight:900;">${promesaReactionTotal(p, 'pasto')}</span></div>`,
                        ];
                        return `<div style="margin-top:.5rem;padding:.32rem .5rem;border-radius:10px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.62rem;text-align:center;">Es tu promesa — no podés reaccionar a vos mismo.</div>
                        <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.38rem;margin-top:.34rem;">${cells.join('')}</div>`;
                    }
                    const socialButtons = reacts.map(r => {
                        const active = p.miReaccion === r.id;
                        const bg = active ? `${r.color}29` : 'rgba(255,255,255,0.055)';
                        const border = active ? `${r.color}cc` : 'rgba(255,255,255,0.1)';
                        return `<button class="btn btn-secondary promesa-react${active ? ' promesa-react-active' : ''}" data-id="${p.id}" data-reaction="${r.id}" data-active="${active ? '1' : '0'}" style="margin:0;min-height:36px;padding:.42rem .26rem;border-radius:999px;background:${bg};border-color:${border};${active ? 'box-shadow:0 0 0 1px ' + r.color + '55 inset;' : ''}display:flex;align-items:center;justify-content:center;gap:.22rem;"><span style="font-size:.72rem;color:var(--white);white-space:nowrap;">${r.label}</span><span style="color:${r.color};font-size:.64rem;font-weight:900;">${promesaReactionTotal(p, r.id)}</span></button>`;
                    }).join('');
                    const pastoBg = p.miAlPasto ? 'rgba(192,132,252,0.22)' : 'rgba(192,132,252,0.07)';
                    const pastoBorder = p.miAlPasto ? 'rgba(192,132,252,0.6)' : 'rgba(192,132,252,0.16)';
                    const pastoShadow = p.miAlPasto ? 'box-shadow:0 0 0 1px rgba(192,132,252,.35) inset;' : '';
                    const pastoLabel = p.miAlPasto ? '✓ Reportado' : '🥾 Al pasto';
                    return `<div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.38rem;margin-top:.55rem;">${socialButtons}<button class="btn btn-secondary promesa-react${p.miAlPasto ? ' promesa-react-active' : ''}" data-id="${p.id}" data-reaction="pasto" data-active="${p.miAlPasto ? '1' : '0'}" style="margin:0;min-height:36px;padding:.42rem .26rem;border-radius:999px;background:${pastoBg};border-color:${pastoBorder};${pastoShadow}color:#ddd6fe;display:flex;align-items:center;justify-content:center;gap:.18rem;font-size:.7rem;">${pastoLabel} <span style="color:#c084fc;font-size:.64rem;font-weight:900;">${promesaReactionTotal(p, 'pasto')}</span></button></div>`;
                })() : ''}

                ${p.estado === 'validacion' && !isCreator ? `
                    <button class="btn btn-primary promesa-validate" data-id="${p.id}" style="margin-top: 0.5rem; padding: 0.58rem; min-height:40px; background: linear-gradient(135deg, #22c55e 0%, #75aadb 100%); border: none;">✅ Validado</button>
                ` : ''}

                ${canResolvePromise ? `
                    <div style="margin-top: 0.5rem; display:grid; grid-template-columns:${canExtendPromise ? '1fr 1fr 1fr' : '1fr 1fr'}; gap:0.38rem;">
                        <button class="btn btn-primary promesa-resolve" data-id="${p.id}" data-resolution="cumplida" style="margin:0;min-height:36px;padding:.42rem .35rem;font-size:.7rem;background:linear-gradient(135deg,#22c55e 0%,#75aadb 100%);border:none;">Cumplí</button>
                        ${canExtendPromise ? `<button class="btn btn-secondary promesa-extension-open" data-id="${p.id}" style="margin:0;min-height:36px;padding:.42rem .35rem;font-size:.7rem;background:rgba(117,170,219,.08);border-color:rgba(117,170,219,.18);">Extender</button>` : ''}
                        <button class="btn btn-secondary promesa-resolve" data-id="${p.id}" data-resolution="incumplida" style="margin:0;min-height:36px;padding:.42rem .35rem;font-size:.7rem;color:#fecaca;background:rgba(239,68,68,.08);border-color:rgba(239,68,68,.18);">Incumplí</button>
                    </div>
                    ${canExtendPromise ? `
                        <div class="promesa-extension-panel" data-id="${p.id}" style="display:none;grid-template-columns:1fr auto;gap:.38rem;margin-top:.42rem;">
                            <select class="promesa-extension-select" data-id="${p.id}" style="min-width:0;border-radius:999px;border:1px solid rgba(255,255,255,.12);background:rgba(0,0,0,.18);color:var(--white);padding:.46rem .58rem;font-size:.72rem;">
                                ${promiseDeadlineOptions.map(opt => `<option value="${opt.ms}|${opt.label}">${opt.label}</option>`).join('')}
                            </select>
                            <button class="btn btn-secondary promesa-extension-apply" data-id="${p.id}" style="width:auto;margin:0;min-height:34px;padding:.42rem .58rem;border-radius:999px;font-size:.7rem;background:rgba(117,170,219,.1);border-color:rgba(117,170,219,.2);">Aplicar</button>
                        </div>
                    ` : ''}
                ` : ''}
                </div>
            </div>
        `;
    };

    const promesasHTML = `
        <div style="margin-bottom: 0.78rem;">
            <div class="glass-card" style="padding: ${canCreatePromesa ? '0.78rem' : '0.48rem'}; border-color: rgba(245,158,11,0.24); background: linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(255,255,255,0.035) 100%);">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: ${canCreatePromesa ? '0.58rem' : '0'};">
                    <div>
                        ${canCreatePromesa
                            ? `<strong style="display: block; color: var(--white); font-size: 0.94rem;">🙏 Nueva promesa</strong>`
                            : `<button type="button" disabled style="min-height:34px;border:1px solid rgba(245,158,11,.2);border-radius:999px;background:rgba(255,255,255,.045);color:var(--white);padding:.34rem .62rem;font-size:.78rem;font-weight:900;opacity:1;">🙏 Nueva promesa · ${userOpenPromises.length}/3 activas</button>`}
                        ${canCreatePromesa ? `<span style="display: block; color: var(--text-muted); font-size: 0.7rem; line-height: 1.25; margin-top: 0.18rem;">Condición, compromiso, plazo y consecuencia.</span>` : ''}
                    </div>
                    ${canCreatePromesa ? `<button type="button" disabled style="flex: 0 0 auto; color: #86efac; font-size: 0.68rem; font-weight: 900; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; padding: 0.24rem 0.5rem; opacity: 1;">${userOpenPromises.length}/3 activas</button>` : ''}
                </div>
                ${canCreatePromesa ? `
                    <form id="promesaForm" style="display: flex; flex-direction: column; gap: 0.55rem;">
                        <label style="display: grid; gap: 0.24rem;">
                            <span style="color: #fcd34d; font-size: 0.64rem; text-transform: uppercase; font-weight: 900;">Si sucede...</span>
                            <input id="promesaCondicion" maxlength="110" required placeholder="Argentina sale campeón" style="border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.64rem 0.72rem; font-size: 0.84rem;">
                        </label>
                        <label style="display: grid; gap: 0.24rem;">
                            <span style="color: #fcd34d; font-size: 0.64rem; text-transform: uppercase; font-weight: 900;">Me comprometo a...</span>
                            <input id="promesaCompromiso" maxlength="110" required placeholder="raparme la cabeza" style="border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.64rem 0.72rem; font-size: 0.84rem;">
                        </label>
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;">
                            <select id="promesaDeadline" style="min-width: 0; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.58rem 0.62rem; font-size: 0.78rem;">
                                ${promiseDeadlineOptions.map(opt => `<option value="${opt.ms}|${opt.label}">${opt.label}</option>`).join('')}
                                <option value="custom|Fecha personalizada">Fecha personalizada</option>
                            </select>
                            <select id="promesaVisibility" style="min-width: 0; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.58rem 0.62rem; font-size: 0.78rem;">
                                <option value="grupo">🏠 Privada grupo</option>
                                <option value="secreta">🔒 Secreta</option>
                                <option value="publica">🌎 Pública</option>
                            </select>
                        </div>
                        <input id="promesaCustomDate" type="date" style="display: none; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.18); color: var(--white); padding: 0.58rem 0.62rem; font-size: 0.78rem;">
                        <button class="btn btn-primary" type="submit" style="margin: 0; padding: 0.68rem; min-height: 42px; background: linear-gradient(135deg, #f59e0b 0%, #a855f7 100%); border: none;">Crear promesa</button>
                    </form>
                ` : ''}
            </div>
        </div>

        ${promesasPendientesResolucion.length ? `
            <h3 style="font-size: 0.86rem; color: var(--white); margin: 0.8rem 0 0.5rem;">Pendientes de validación</h3>
            <div style="display: flex; flex-direction: column; gap: 0.62rem; margin-bottom: 1rem;">
                ${promesasPendientesResolucion.map(p => renderPromesaCard(p)).join('')}
            </div>
        ` : ''}

        <h3 style="font-size: 0.86rem; color: var(--white); margin: 0.8rem 0 0.5rem;">Promesas activas</h3>
        <div style="display: flex; flex-direction: column; gap: 0.62rem; margin-bottom: 1rem;">
            ${promesasActivas.length ? promesasActivas.map(p => renderPromesaCard(p)).join('') : `<div class="glass-card" style="padding: 0.78rem; color: var(--text-muted); font-size: 0.8rem;">Todavía no hay promesas activas en este grupo.</div>`}
        </div>

        <h3 style="font-size: 0.86rem; color: var(--white); margin: 0.8rem 0 0.5rem;">Memoria reciente</h3>
        <div style="display: flex; flex-direction: column; gap: 0.62rem; margin-bottom: 2rem;">
            ${promesasCerradas.length ? promesasCerradas.slice(0, 4).map(p => renderPromesaCard(p)).join('') : `<div class="glass-card" style="padding: 0.78rem; color: var(--text-muted); font-size: 0.8rem;">Las promesas cerradas van a vivir acá como memoria del grupo.</div>`}
        </div>
    `;

    root.innerHTML = `
        <div class="view-enter" style="width: 100%; animation: fadeIn 0.4s ease-out forwards;">
            ${renderActiveGroupBar(codigo)}
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.1rem;">
                <button class="btn btn-secondary" style="padding: 0.55rem 0.85rem; margin: 0; width: auto;" onclick="window.location.hash='/g/${codigo}'">←</button>
                <div style="min-width: 0;">
                    <h2 style="font-family: var(--font-heading); font-size: 1.55rem; text-transform: uppercase; color: var(--white); margin: 0;">⚽ Rituales del grupo</h2>
                    <p style="font-size: 0.82rem; color: var(--text-muted); margin: 0.25rem 0 0;">El folklore y la emoción de ${grupo.nombre} durante el mundial.</p>
                </div>
            </div>

            <div style="display: flex; gap: 0.45rem; margin-bottom: 1rem; padding: 0.28rem; border-radius: 999px; background: rgba(255,255,255,0.045); border: 1px solid rgba(255,255,255,0.08);">
                ${tabButton('arengas', '📢', 'Arengas', arengas.length)}
                ${tabButton('cabalas', '🔮', 'Cábalas', activeCabalas.length)}
                ${tabButton('promesas', '🙏', 'Promesas', promesasAbiertas.length)}
            </div>

            ${tab === 'arengas' ? chatPanelHTML + arengasHTML : ''}
            ${tab === 'cabalas' ? cabalasHTML : ''}
            ${tab === 'promesas' ? promesasHTML : ''}
        </div>
        ${renderBottomNav(codigo, 'grupo')}
    `;

    // ─── PORTAL "Crear arenga" (fix stacking context · jun-2026) ────────────
    // El overlay vive dentro de .view-enter (que es stacking context por
    // animation:fadeIn opacity) y el guardian embebe ese .view-enter en el
    // home → el modal queda ATRAPADO en un contexto hijo: el cartel del pacto
    // certificado (grupo.js:412) y "Cerrar sesión" (grupo.js:414) son hermanos
    // POSTERIORES del <section> embebido y pintan encima, interceptando el tap
    // de Publicar. Solución: relocalizar el NODO INTACTO del overlay a <body>
    // (contexto raíz) → su z-index:110 ordena global y tapa todo (incl. la
    // bottom-nav z-100). NO se reconstruye markup: la .glass-card scroller de
    // 8f7ff93 viaja tal cual (sin transform/filter/will-change, sin dvh/vh).
    // Los handlers del modal son document-scoped (getElementById/querySelectorAll)
    // → sobreviven al move. El cierre se cablea acá (el onclick inline se quitó):
    // embebido → re-embed del guardian (queda en el home); aislado → hash nav.
    document.querySelectorAll('body > [data-arenga-overlay]').forEach(n => n.remove()); // sin leaks/duplicados
    const crearArengaOverlay = document.getElementById('arengaCreateOverlay');
    if (crearArengaOverlay) {
        crearArengaOverlay.dataset.arengaOverlay = '1';
        document.body.appendChild(crearArengaOverlay); // relocaliza el nodo INTACTO al body
        const cerrarCrearArenga = () => {
            document.querySelectorAll('body > [data-arenga-overlay]').forEach(n => n.remove());
            if (reRenderTarget(options) === 'reembed') {
                window.__pmReembedRituales(codigo, 'arengas'); // embebido: re-embed sin crear → queda en el home
            } else {
                window.location.hash = `/rituales/${codigo}/arengas`; // aislado: dropea ?crear=1 → re-render sin modal
            }
        };
        crearArengaOverlay.addEventListener('click', (e) => { if (e.target === crearArengaOverlay) cerrarCrearArenga(); });
        crearArengaOverlay.querySelector('[data-arenga-close]')?.addEventListener('click', (e) => { e.preventDefault(); cerrarCrearArenga(); });
    }

    // ─── Handlers (capa de datos contra Postgres; F1.3.d1) ──────────────────

    document.querySelectorAll('.arenga-open').forEach(button => {
        button.addEventListener('click', () => {
            window.location.hash = `/rituales/${codigo}/arengas?arenga=${button.dataset.id}`;
        });
    });

    const btnMockArenga = document.getElementById('btnMockArenga');
    if (btnMockArenga) {
        btnMockArenga.addEventListener('click', () => {
            window.location.hash = `/rituales/${codigo}/arengas?crear=1`;
        });
    }

    const createSheet = document.getElementById('arengaRecordBtn');
    if (createSheet) {
        // Spec arengas v1.2 §2: default al primer tipo (vamos).
        let selectedCreateType = 'vamos';
        let selectedCreateVisibility = 'grupo';
        // Interruptor "Tribuna Libre": si está ON, al elegir "Pública" mostramos
        // el cartel de la ventana gratis. Lectura best-effort (degrada a OFF); el
        // gate real (permitir/rechazar la pública) vive server-side en el RPC.
        let tribunaLibreOn = false;
        const syncTribunaLibreCartel = () => {
            const cartel = document.querySelector('.arenga-tribuna-libre-cartel');
            if (cartel) cartel.style.display = (tribunaLibreOn && selectedCreateVisibility === 'publica') ? 'block' : 'none';
        };
        getTribunaLibre().then((r) => { tribunaLibreOn = Boolean(r?.enabled); syncTribunaLibreCartel(); }).catch(() => {});
        // F1.4.a: grabador real con MediaRecorder. Reemplaza el setInterval
        // cosmético que vivía acá hasta F1.3.d1. Estados: idle / grabando /
        // procesando / listo / error.
        let recordingHandle = null;
        let recordedBlob = null;
        let recordedMimeType = null;
        let recordedSeconds = 0;
        let previewObjectUrl = null;
        let previewAudio = null;

        const recordBtn = document.getElementById('arengaRecordBtn');
        const recordProgress = document.getElementById('arengaRecordProgress');
        const recordTimer = document.getElementById('arengaRecordTimer');
        const recordStatus = document.getElementById('arengaRecordStatus');
        const previewActions = document.getElementById('arengaPreviewActions');
        const publishBtn = document.getElementById('arengaPublishBtn');
        const retryBtn = document.getElementById('arengaRetryBtn');
        const previewBtn = document.getElementById('arengaPreviewBtn');
        const maxSec = getMaxDuration();

        const setRecordingUI = (seconds, status, disablePublish = true) => {
            recordedSeconds = seconds;
            if (recordTimer) recordTimer.textContent = `00:${String(seconds).padStart(2, '0')} / 00:${String(maxSec).padStart(2, '0')}`;
            if (recordProgress) recordProgress.style.width = `${Math.min(100, (seconds / maxSec) * 100)}%`;
            if (recordStatus) recordStatus.textContent = status;
            if (publishBtn) {
                publishBtn.disabled = disablePublish;
                publishBtn.style.opacity = disablePublish ? '.45' : '1';
            }
        };

        const showPreviewActions = (show) => {
            if (previewActions) previewActions.style.display = show ? 'grid' : 'none';
        };

        const traducirErrorGrabador = (err) => {
            const code = err?.code || '';
            if (code === 'mic_permission_denied') return 'Permiso de micrófono denegado. Activalo desde la configuración del browser.';
            if (code === 'mic_unavailable') return 'No detectamos un micrófono.';
            if (code === 'unsupported_browser') return 'Tu navegador no soporta grabación de audio.';
            if (code === 'size_exceeded') return 'El audio supera el límite de 5 MB.';
            if (code === 'upload_failed') return 'No pudimos subir el audio. Probá de nuevo.';
            return err?.message || 'No pudimos terminar la grabación.';
        };

        const resetPreview = () => {
            if (previewAudio) { try { previewAudio.pause(); } catch (e) {} previewAudio.src = ''; previewAudio = null; }
            if (previewObjectUrl) { URL.revokeObjectURL(previewObjectUrl); previewObjectUrl = null; }
            recordedBlob = null;
            recordedMimeType = null;
        };

        const finalizeRecording = async (handle) => {
            try {
                const { blob, mimeType, duracionMs } = await handle.stop();
                recordingHandle = null;
                recordedBlob = blob;
                recordedMimeType = mimeType;
                const finalSec = Math.min(maxSec, Math.max(1, Math.round(duracionMs / 1000)));
                setRecordingUI(finalSec, 'Preview listo', false);
                recordBtn.textContent = 'Volver a grabar';
                showPreviewActions(true);
            } catch (err) {
                recordingHandle = null;
                setRecordingUI(0, '⚠️ ' + traducirErrorGrabador(err), true);
                recordBtn.textContent = 'Grabar arenga';
                showPreviewActions(false);
            }
        };

        document.querySelectorAll('.arenga-create-type').forEach(button => {
            button.addEventListener('click', () => {
                selectedCreateType = button.dataset.type;
                document.querySelectorAll('.arenga-create-type').forEach(item => {
                    const meta = arengaTypes[item.dataset.type] || arengaTypes.vamos;
                    const active = item === button;
                    item.classList.toggle('active', active);
                    item.style.background = active ? `${meta.color}1f` : 'rgba(255,255,255,.045)';
                    item.style.borderColor = active ? `${meta.color}80` : 'rgba(255,255,255,.1)';
                });
            });
        });

        document.querySelectorAll('.arenga-create-visibility').forEach(button => {
            button.addEventListener('click', () => {
                selectedCreateVisibility = button.dataset.visibility;
                document.querySelectorAll('.arenga-create-visibility').forEach(item => {
                    const active = item === button;
                    item.classList.toggle('active', active);
                    item.style.background = active ? 'rgba(117,170,219,.12)' : 'rgba(255,255,255,.045)';
                    item.style.borderColor = active ? 'rgba(117,170,219,.32)' : 'rgba(255,255,255,.1)';
                });
                syncTribunaLibreCartel();
            });
        });

        recordBtn.addEventListener('click', async () => {
            if (recordingHandle) {
                // Estado: grabando → finalizar manualmente.
                await finalizeRecording(recordingHandle);
                return;
            }
            // Estado: idle → arrancar grabador real.
            resetPreview();
            setRecordingUI(0, 'Pidiendo micrófono…', true);
            recordBtn.disabled = true;
            try {
                const handle = await startRecording({
                    onTick: (sec) => {
                        setRecordingUI(sec, 'Grabando…', true);
                    },
                    autoStopAfterMs: maxSec * 1000,
                    // Auto-corte al límite: el recorder se frena solo a los 20s
                    // y avisa por acá. Finalizamos por el MISMO camino que el
                    // "Finalizar" manual (handle.stop ya es idempotente y el
                    // blob queda ensamblado) y mostramos el aviso de límite.
                    // Reemplaza el viejo setInterval (pollAutoStop) que competía
                    // con el auto-corte interno y terminaba perdiendo el audio.
                    onAutoStop: async () => {
                        if (recordingHandle !== handle) return;
                        await finalizeRecording(handle);
                        if (recordStatus) recordStatus.textContent = '⏱️ Cortamos a los 20s — listo para enviar';
                    },
                });
                recordingHandle = handle;
                recordBtn.disabled = false;
                recordBtn.textContent = 'Finalizar';
                setRecordingUI(0, 'Grabando…', true);
                showPreviewActions(false);
            } catch (err) {
                recordingHandle = null;
                recordBtn.disabled = false;
                setRecordingUI(0, '⚠️ ' + traducirErrorGrabador(err), true);
                recordBtn.textContent = 'Grabar arenga';
            }
        });

        if (retryBtn) {
            retryBtn.addEventListener('click', () => {
                if (recordingHandle) { try { recordingHandle.cancel(); } catch (e) {} recordingHandle = null; }
                resetPreview();
                setRecordingUI(0, 'Listo para grabar', true);
                recordBtn.textContent = 'Grabar arenga';
                showPreviewActions(false);
            });
        }

        if (previewBtn) {
            previewBtn.addEventListener('click', () => {
                if (!recordedBlob) return;
                if (!previewObjectUrl) previewObjectUrl = URL.createObjectURL(recordedBlob);
                if (!previewAudio) {
                    previewAudio = new Audio(previewObjectUrl);
                    previewAudio.preload = 'metadata';
                    previewAudio.onended = () => { if (recordStatus) recordStatus.textContent = 'Preview listo'; };
                }
                try {
                    previewAudio.currentTime = 0;
                    previewAudio.play();
                    if (recordStatus) recordStatus.textContent = 'Reproduciendo preview…';
                } catch (e) {
                    if (recordStatus) recordStatus.textContent = '⚠️ No pudimos reproducir el preview.';
                }
            });
        }

        if (publishBtn) {
            publishBtn.addEventListener('click', async () => {
                if (!recordedBlob || !recordedMimeType) return;
                const seconds = Math.min(Math.max(recordedSeconds, 1), maxSec);
                publishBtn.disabled = true;
                publishBtn.style.opacity = '.45';
                const oldLabel = publishBtn.textContent;
                publishBtn.textContent = 'Publicando…';
                if (recordStatus) recordStatus.textContent = 'Subiendo audio…';

                const audioId = generateAudioId();
                try {
                    // 1) Subir el blob al bucket público con path estable.
                    const { publicUrl } = await uploadArenga(recordedBlob, recordedMimeType, groupId, audioId);
                    // 2) Crear la fila pasando audio_id (mismo del path) + audio_url.
                    if (recordStatus) recordStatus.textContent = 'Guardando arenga…';
                    // Spec arengas v1.2 §4 paso 4: título opcional max 20 chars.
                    // Si está vacío, se guarda null y la UI cae al meta.label
                    // del tipo (en renderArengaDetail y vistas de Tribuna).
                    const tituloInput = document.getElementById('arengaTituloInput');
                    const tituloRaw = (tituloInput?.value || '').trim();
                    const tituloFinal = tituloRaw.length > 0 ? tituloRaw.slice(0, 20) : null;
                    const r = await createArenga(groupId, jugadorActivo.id, {
                        tipo_arenga: selectedCreateType,
                        titulo: tituloFinal,
                        es_publica: selectedCreateVisibility === 'publica',
                        duracion_segundos: seconds,
                        audio_url: publicUrl,
                        audio_id: audioId,
                    });
                    if (r.error) {
                        publishBtn.disabled = false;
                        publishBtn.style.opacity = '1';
                        publishBtn.textContent = oldLabel;
                        alert('⚠️ ' + traducirErrorRitual(r.error));
                        if (recordStatus) recordStatus.textContent = 'Preview listo';
                        return;
                    }
                    resetPreview();
                    // Nav post-publicar: preservar el contexto embebido (home) igual
                    // que cábalas/promesas (fix 98b41c1). Embebido → re-embed del
                    // guardian (queda en el home); aislado → hash nav (limpia ?crear
                    // y re-pinta la vista de rituales). Antes hacía siempre el hash
                    // nav → desde el home pisaba con la vista aislada (BUG arengas).
                    if (options.target) {
                        await reRenderRituales('arengas');
                    } else {
                        window.location.hash = `/rituales/${codigo}/arengas`;
                    }
                } catch (err) {
                    publishBtn.disabled = false;
                    publishBtn.style.opacity = '1';
                    publishBtn.textContent = oldLabel;
                    alert('⚠️ ' + traducirErrorGrabador(err));
                    if (recordStatus) recordStatus.textContent = 'Preview listo';
                }
            });
        }
    }

    document.querySelectorAll('.arenga-react').forEach(button => {
        button.addEventListener('click', async () => {
            if (button.disabled) return;
            const reaction = button.dataset.reaction;
            const isActive = button.dataset.active === '1';
            const arengaId = button.dataset.id;
            // Spec arengas v1.2 §9: al_pasto es 1 reporte por jugador, no
            // acumulable. La DB lo rechaza por UNIQUE pero el optimistic +1
            // del front lo inflaba. Bloqueo en cliente si ya reporté: clicks
            // repetidos son no-op.
            if (reaction === 'pasto' && isActive) return;
            // Optimistic update: aplicar delta al DOM antes del RPC para que el
            // usuario vea el cambio inmediato. Si el RPC falla, revertimos.
            // 'pasto' es acumulativo (no toca data-active); los 3 positivos
            // hacen toggle/reemplazo (spec arengas v1.2 §10 reglas 3-4).
            const prevActive = document.querySelector(`.arenga-react[data-id="${arengaId}"][data-active="1"]`);
            const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null;
            const deltas = computeReactionDelta({
                clickedReaction: reaction,
                isActiveOnClicked: isActive,
                prevActiveReaction,
            });
            applyReactionDelta({ scope: document, selector: '.arenga-react', arengaId, deltas });
            const sumaDeltaA = deltas.reduce((s, d) => s + d.delta, 0);
            refrescarContadoresRitual('arenga', arengaId, sumaDeltaA);
            button.disabled = true;
            button.style.opacity = '.5';
            let r;
            if (reaction === 'pasto') {
                r = await reaccionar(jugadorActivo.id, 'arenga', arengaId, 'al_pasto');
            } else if (isActive) {
                r = await quitarReaccion(jugadorActivo.id, 'arenga', arengaId);
            } else {
                r = await reaccionar(jugadorActivo.id, 'arenga', arengaId, reaction);
            }
            if (r.error) {
                // Revert optimistic.
                applyReactionDelta({ scope: document, selector: '.arenga-react', arengaId, deltas: invertDeltas(deltas) });
                refrescarContadoresRitual('arenga', arengaId, -sumaDeltaA);
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorRitual(r.error));
                return;
            }
            // Sin re-render full: el optimistic ya pintó el delta y reaccionar()
            // invalida la cache. El render fresco lo hace la próxima visita al
            // módulo. El re-render que estaba acá causaba flash de la card.
            button.disabled = false;
            button.style.opacity = '1';
            if (reaction === 'pasto') {
                // Tras el primer reporte: bloquear futuros clicks + UI
                // "✓ Reportado". El UNIQUE en DB ya hace idempotente al INSERT;
                // el front replica el bloqueo para no inflar el contador.
                button.dataset.active = '1';
                button.style.background = 'rgba(192,132,252,0.22)';
                button.style.borderColor = 'rgba(192,132,252,0.6)';
                button.style.boxShadow = '0 0 0 1px rgba(192,132,252,.35) inset';
                const labelSpan = button.querySelector('span');
                if (labelSpan) labelSpan.textContent = '✓ Reportado';
            } else {
                // Toggle visual: el botón clickeado pasa a activo; los demás (del
                // mismo arengaId) se apagan. 'pasto' es acumulativo, no toca data-active.
                document.querySelectorAll(`.arenga-react[data-id="${arengaId}"]`).forEach((b) => {
                    if (b.dataset.reaction === 'pasto') return;
                    b.dataset.active = (b === button && !isActive) ? '1' : '0';
                });
            }
        });
    });

    document.querySelectorAll('.arenga-save').forEach(button => {
        button.addEventListener('click', async () => {
            if (button.disabled) return;
            if (!currentUser) { alert('Iniciá sesión para guardar arengas.'); return; }
            // Spec arengas v1.2 §10 regla 13: toggle guardar/desguardar.
            const arengaId = button.dataset.id;
            const isSaved = button.dataset.saved === '1';
            // Optimistic: cambiar el texto y data-saved del botón antes del RPC.
            button.dataset.saved = isSaved ? '0' : '1';
            button.textContent = isSaved ? 'Guardar arenga' : '✓ Guardada';
            button.disabled = true;
            button.style.opacity = '.5';
            const r = isSaved
                ? await quitarArengaGuardada(currentUser.user_id, arengaId)
                : await guardarArenga(currentUser.user_id, arengaId);
            if (r.error) {
                // Revert.
                button.dataset.saved = isSaved ? '1' : '0';
                button.textContent = isSaved ? '✓ Guardada' : 'Guardar arenga';
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorRitual(r.error));
                return;
            }
            await reRenderRituales('arengas');
        });
    });

    // F1.4.a: <audio> real con preload="metadata". Si no hay audio_url
    // (arenga sin audio aún subido), el botón queda disabled desde el HTML.
    const arengaAudio = document.getElementById('arengaAudio');
    const arengaPlayBtn = document.getElementById('arengaPlayBtn');
    const arengaProgress = document.getElementById('arengaProgress');
    if (arengaAudio && arengaPlayBtn && arengaProgress && selectedArenga) {
        // Bug Duration=0 v3: para .webm con Duration=0 explícito en EBML, el
        // browser en PROD nunca entra en "playing state" aunque .play() resuelva
        // — queda en waiting porque no sabe cuándo termina el stream. Solución:
        // bajar el archivo completo a un blob URL local (en el primer click)
        // y reasignar el src — el browser tiene los Clusters disponibles y puede
        // reproducir sin importar el header roto. Wall-clock con Date.now()
        // cubre la barra mientras audio.currentTime no avanza para esos casos.
        let progressRafId = null;
        let progressStartMs = 0;   // wall-clock al iniciar/resumir play
        let progressAccumMs = 0;   // tiempo acumulado (resume tras pause)
        const stopProgress = () => {
            if (progressRafId !== null) { cancelAnimationFrame(progressRafId); progressRafId = null; }
        };
        const cleanupBlob = () => revokeBlobUrl(arengaAudio);
        const finalizeEnded = () => {
            try { arengaAudio.pause(); } catch (_) {}
            stopProgress();
            progressStartMs = 0;
            progressAccumMs = 0;
            arengaPlayBtn.textContent = 'Play';
            arengaProgress.style.width = '0%';
            cleanupBlob();
        };
        const updateProgress = () => {
            const dur = arengaAudio.duration;
            const ct = arengaAudio.currentTime;
            let elapsed, total;
            if (Number.isFinite(dur) && dur > 0 && ct > 0) {
                elapsed = ct; total = dur;
            } else {
                const live = progressStartMs > 0 ? (Date.now() - progressStartMs) : 0;
                elapsed = (progressAccumMs + live) / 1000;
                total = selectedArenga.segundos || 1;
            }
            const pct = Math.min(100, (elapsed / total) * 100);
            arengaProgress.style.width = `${pct}%`;
            // Auto-ended: archivos con Duration=0 tampoco disparan 'ended'.
            if (pct >= 100 && (!Number.isFinite(dur) || dur <= 0)) {
                finalizeEnded();
            }
        };
        const tickProgress = () => {
            updateProgress();
            if (progressRafId !== null && !arengaAudio.paused && !arengaAudio.ended) {
                progressRafId = requestAnimationFrame(tickProgress);
            }
        };
        arengaAudio.addEventListener('pause', () => {
            stopProgress();
            if (progressStartMs > 0) { progressAccumMs += Date.now() - progressStartMs; progressStartMs = 0; }
        });
        arengaAudio.addEventListener('ended', finalizeEnded);
        arengaPlayBtn.addEventListener('click', async () => {
            if (arengaAudio.paused) {
                arengaPlayBtn.textContent = 'Pausa';
                // Bug Duration=0 v3: bajar el archivo a un blob URL local
                // antes del play. Helper compartido en core/audio-player.js.
                const blobResult = await ensureBlobUrl(arengaAudio);
                if (!blobResult.ok && !blobResult.cached) {
                    console.warn('[rituales] ensureBlobUrl:', blobResult.reason);
                }
                // Bug PROD player: <audio> en innerHTML + display:none puede
                // quedar en readyState=0 sin disparar el resource selection
                // algorithm. .load() lo fuerza; idempotente.
                if (arengaAudio.readyState === 0) arengaAudio.load();
                const playPromise = arengaAudio.play();
                if (playPromise && typeof playPromise.catch === 'function') {
                    playPromise.catch((err) => {
                        console.error('[rituales] audio.play():', err);
                        arengaPlayBtn.textContent = 'Play';
                        stopProgress();
                        progressStartMs = 0;
                    });
                }
                // Bug B v2: arrancar rAF desde el click — para archivos con
                // Duration=0 el evento 'play' nunca se dispara.
                progressStartMs = Date.now();
                if (progressRafId === null) progressRafId = requestAnimationFrame(tickProgress);
            } else {
                arengaAudio.pause();
                arengaPlayBtn.textContent = 'Play';
            }
        });
    }

    // Cambiar la visibilidad después de crear quedó deshabilitado (decisión 9-jun):
    // la visibilidad se decide SOLO al crear. Se quitaron los botones "Hacer
    // pública"/"Hacer privada" y su handler. El wrapper toggleArengaVisibilidad y
    // el RPC siguen existiendo (sin caller de UI). Ver DT-tribuna-toggle-feed-sync.

    // Eliminar arenga — solo creador. El RPC borra fila + archivo atómico.
    document.querySelectorAll('.arenga-eliminar').forEach(button => {
        button.addEventListener('click', async () => {
            const ok = window.confirm('¿Eliminar esta arenga? No se puede deshacer.');
            if (!ok) return;
            button.disabled = true;
            const r = await eliminarArengaCompleta(button.dataset.id);
            if (r.error) {
                button.disabled = false;
                alert('⚠️ ' + traducirErrorRitual(r.error));
                return;
            }
            // Nav post-eliminar: mismo patrón que publicar — preservar contexto
            // embebido (home) vs aislado (mismo bug 98b41c1 para arenga).
            if (options.target) {
                await reRenderRituales('arengas');
            } else {
                window.location.hash = `/rituales/${codigo}/arengas`;
            }
        });
    });

    // Spec arengas v1.2 §8: share unificado — abre el mismo modal que
    // "Invitar amigos" del grupo (WhatsApp / Telegram / Copiar / Cancelar).
    // Reemplaza el viejo flow con navigator.share + fallback a clipboard.
    document.querySelectorAll('.arenga-share').forEach(button => {
        button.addEventListener('click', () => {
            const audioId = button.dataset.audioId;
            if (!audioId) return;
            const titulo = button.dataset.titulo || 'arenga mundialista';
            const creador = button.dataset.creador || 'Jugador';
            const url = buildShareUrl(audioId);
            openShareModal({
                title: `Compartir arenga · ${titulo}`,
                subtitle: `De ${creador}`,
                shareUrl: url,
                shareText: `Escuchá la arenga de ${creador}: "${titulo}"`,
            });
        });
    });

    const cabalaForm = document.getElementById('cabalaForm');
    if (cabalaForm) {
        cabalaForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            const text = document.getElementById('cabalaText').value.trim();
            if (!text) return;
            if (text.length > 50) { alert('⚠️ La cábala no puede superar los 50 caracteres.'); return; }
            const [durationMs] = document.getElementById('cabalaDuration').value.split('|');
            const visSel = document.getElementById('cabalaVisibility').value;
            const visibilidad = visSel === 'grupo' ? 'privada' : visSel;
            const r = await createCabala(groupId, jugadorActivo.id, {
                texto: text,
                visibilidad,
                expira_en: new Date(now + Number(durationMs)).toISOString(),
            });
            if (r.error) { alert('⚠️ ' + traducirErrorRitual(r.error)); return; }
            await reRenderRituales('cabalas');
        });
    }

    const promesaDeadline = document.getElementById('promesaDeadline');
    const promesaCustomDate = document.getElementById('promesaCustomDate');
    if (promesaDeadline && promesaCustomDate) {
        promesaDeadline.addEventListener('change', () => {
            promesaCustomDate.style.display = promesaDeadline.value.startsWith('custom') ? 'block' : 'none';
            promesaCustomDate.required = promesaDeadline.value.startsWith('custom');
        });
    }

    const promesaForm = document.getElementById('promesaForm');
    if (promesaForm) {
        promesaForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            const condicion = document.getElementById('promesaCondicion').value.trim();
            const compromiso = document.getElementById('promesaCompromiso').value.trim();
            if (!condicion || !compromiso) return;

            const deadlineValue = document.getElementById('promesaDeadline').value;
            const visSel = document.getElementById('promesaVisibility').value;
            const visibilidad = visSel === 'grupo' ? 'privada' : visSel;
            let deadlineAt;

            if (deadlineValue.startsWith('custom')) {
                const customDate = document.getElementById('promesaCustomDate').value;
                if (!customDate) return;
                deadlineAt = new Date(`${customDate}T23:59:00`);
            } else {
                const [deadlineMs] = deadlineValue.split('|');
                deadlineAt = new Date(now + Number(deadlineMs));
            }

            const r = await createPromesa(groupId, jugadorActivo.id, {
                condicion,
                compromiso,
                visibilidad,
                deadline_at: deadlineAt.toISOString(),
            });
            if (r.error) { alert('⚠️ ' + traducirErrorRitual(r.error)); return; }
            await reRenderRituales('promesas');
        });
    }

    // Handler cábala-react (NUEVO, auditoría 28-may): optimistic + RPC sin
    // re-render full. Spec Cábalas v1.0 §5: 1 reacción por cábala, elige
    // entre 2. Si vuelve a clickear la misma → toggle off (quitarReaccion).
    // Si clickea la otra → reemplazo (RPC `reaccionar` borra la previa).
    document.querySelectorAll('.cabala-react').forEach(button => {
        button.addEventListener('click', async () => {
            if (button.disabled) return;
            const reaction = button.dataset.reaction;
            const isActive = button.dataset.active === '1';
            const cabalaId = button.dataset.id;
            const prevActive = document.querySelector(`.cabala-react[data-id="${cabalaId}"][data-active="1"]`);
            const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null;
            const deltas = computeReactionDelta({ clickedReaction: reaction, isActiveOnClicked: isActive, prevActiveReaction });
            applyReactionDelta({ scope: document, selector: '.cabala-react', arengaId: cabalaId, deltas });
            const sumaDeltaC = deltas.reduce((s, d) => s + d.delta, 0);
            refrescarContadoresRitual('cabala', cabalaId, sumaDeltaC);
            button.disabled = true;
            button.style.opacity = '.5';
            let r;
            if (isActive) r = await quitarReaccion(jugadorActivo.id, 'cabala', cabalaId);
            else r = await reaccionar(jugadorActivo.id, 'cabala', cabalaId, reaction);
            if (r.error) {
                applyReactionDelta({ scope: document, selector: '.cabala-react', arengaId: cabalaId, deltas: invertDeltas(deltas) });
                refrescarContadoresRitual('cabala', cabalaId, -sumaDeltaC);
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorRitual(r.error));
                return;
            }
            button.disabled = false;
            button.style.opacity = '1';
            document.querySelectorAll(`.cabala-react[data-id="${cabalaId}"]`).forEach(b => {
                b.dataset.active = (b === button && !isActive) ? '1' : '0';
            });
        });
    });

    // Handler promesa-react (REESCRITO, auditoría 28-may): optimistic + bloqueo
    // Al pasto si ya reporté (1 por jugador §15). Sin re-render full → la card
    // no parpadea. Spec Promesas v1.0 §6: 3 reacciones (Me sumo / Jua / Al pasto).
    document.querySelectorAll('.promesa-react').forEach(button => {
        button.addEventListener('click', async () => {
            if (button.disabled) return;
            const reaction = button.dataset.reaction;
            const isActive = button.dataset.active === '1';
            const promesaId = button.dataset.id;
            if (reaction === 'pasto' && isActive) return; // bloqueo idempotente
            const prevActive = document.querySelector(`.promesa-react[data-id="${promesaId}"][data-active="1"]`);
            const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null;
            const deltas = computeReactionDelta({ clickedReaction: reaction, isActiveOnClicked: isActive, prevActiveReaction });
            applyReactionDelta({ scope: document, selector: '.promesa-react', arengaId: promesaId, deltas });
            const sumaDeltaP = deltas.reduce((s, d) => s + d.delta, 0);
            refrescarContadoresRitual('promesa', promesaId, sumaDeltaP);
            button.disabled = true;
            button.style.opacity = '.5';
            const tipoReaccion = reaction === 'pasto' ? 'al_pasto' : reaction;
            let r;
            if (reaction === 'pasto') r = await reaccionar(jugadorActivo.id, 'promesa', promesaId, 'al_pasto');
            else if (isActive) r = await quitarReaccion(jugadorActivo.id, 'promesa', promesaId);
            else r = await reaccionar(jugadorActivo.id, 'promesa', promesaId, tipoReaccion);
            if (r.error) {
                applyReactionDelta({ scope: document, selector: '.promesa-react', arengaId: promesaId, deltas: invertDeltas(deltas) });
                refrescarContadoresRitual('promesa', promesaId, -sumaDeltaP);
                button.disabled = false;
                button.style.opacity = '1';
                alert('⚠️ ' + traducirErrorRitual(r.error));
                return;
            }
            button.disabled = false;
            button.style.opacity = '1';
            if (reaction === 'pasto') {
                button.dataset.active = '1';
                button.style.background = 'rgba(192,132,252,0.22)';
                button.style.borderColor = 'rgba(192,132,252,0.6)';
                button.style.boxShadow = '0 0 0 1px rgba(192,132,252,.35) inset';
                button.innerHTML = button.innerHTML.replace('🥾 Al pasto', '✓ Reportado');
            } else {
                document.querySelectorAll(`.promesa-react[data-id="${promesaId}"]`).forEach(b => {
                    if (b.dataset.reaction === 'pasto') return;
                    b.dataset.active = (b === button && !isActive) ? '1' : '0';
                });
            }
        });
    });

    document.querySelectorAll('.promesa-resolve').forEach(button => {
        button.addEventListener('click', async () => {
            const resolution = button.dataset.resolution;
            // "Cumplí" → queda pendiente de validación grupal; "Incumplí" → cerrada.
            const estado = resolution === 'cumplida' ? 'pendiente_validacion' : 'incumplida';
            const r = await declararPromesa(button.dataset.id, estado);
            if (r.error) { alert('⚠️ ' + traducirErrorRitual(r.error)); return; }
            await reRenderRituales('promesas');
        });
    });

    document.querySelectorAll('.promesa-extension-open').forEach(button => {
        button.addEventListener('click', () => {
            const panel = document.querySelector(`.promesa-extension-panel[data-id="${button.dataset.id}"]`);
            if (!panel) return;
            panel.style.display = 'grid';
            button.textContent = 'Elegir plazo';
        });
    });

    document.querySelectorAll('.promesa-extension-apply').forEach(button => {
        button.addEventListener('click', async () => {
            const select = document.querySelector(`.promesa-extension-select[data-id="${button.dataset.id}"]`);
            const [msValue, label = 'tiempo extra'] = (select?.value || '').split('|');
            const extraMs = Number(msValue);
            if (!Number.isFinite(extraMs) || extraMs <= 0) return;
            const r = await extenderPromesa(button.dataset.id, {
                deadline_at: new Date(now + extraMs).toISOString(),
                extension_label: label,
            });
            if (r.error) { alert('⚠️ ' + traducirErrorRitual(r.error)); return; }
            await reRenderRituales('promesas');
        });
    });

    document.querySelectorAll('.promesa-validate').forEach(button => {
        button.addEventListener('click', async () => {
            const r = await marcarCumplida(button.dataset.id, jugadorActivo.id, 'El grupo da fe');
            if (r.error) { alert('⚠️ ' + traducirErrorRitual(r.error)); return; }
            await reRenderRituales('promesas');
        });
    });

    // Promesas: acordeón — el header colapsa/expande la card; solo UNA abierta a
    // la vez. Los botones de acción (Cumplí/Extender/Incumplí/Validado) y de
    // reacción viven en el body y conservan sus handlers — el toggle está en el
    // header (nodo hermano del body, no su ancestro), así que clickear un botón
    // del body no dispara el toggle.
    document.querySelectorAll('[data-promesa-toggle]').forEach(header => {
        header.addEventListener('click', () => {
            const card = header.closest('.promesa-card');
            if (!card) return;
            const willOpen = !card.classList.contains('open');
            document.querySelectorAll('.promesa-card.open').forEach(c => {
                c.classList.remove('open');
                const b = c.querySelector('.promesa-card-body'); if (b) b.style.display = 'none';
                const ch = c.querySelector('.promesa-chevron'); if (ch) ch.style.transform = 'rotate(0deg)';
                const h = c.querySelector('.promesa-card-header'); if (h) h.setAttribute('aria-expanded', 'false');
            });
            if (willOpen) {
                card.classList.add('open');
                const b = card.querySelector('.promesa-card-body'); if (b) b.style.display = 'block';
                const ch = card.querySelector('.promesa-chevron'); if (ch) ch.style.transform = 'rotate(180deg)';
                header.setAttribute('aria-expanded', 'true');
            }
        });
    });

    // Chat del grupo — envío de mensajes. El panel <details> recuerda su estado
    // tras el re-render reabriéndose si estaba abierto.
    const chatForm = document.getElementById('chatForm');
    if (chatForm) {
        chatForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            const input = document.getElementById('chatInput');
            const texto = (input?.value || '').trim();
            if (!texto) return;
            const sendBtn = chatForm.querySelector('button[type="submit"]');
            if (sendBtn) sendBtn.disabled = true;
            const r = await enviarMensaje(groupId, jugadorActivo.id, texto);
            if (r.error) {
                if (sendBtn) sendBtn.disabled = false;
                alert('⚠️ ' + traducirErrorRitual(r.error));
                return;
            }
            await reRenderRituales('arengas');
            const panel = root.querySelector('#chatPanel');
            if (panel) panel.open = true;
            const box = root.querySelector('#chatMensajes');
            if (box) box.scrollTop = box.scrollHeight;
            root.querySelector('#chatInput')?.focus();
        });
    }
};

// ─── Bridge transitorio a window.* (sub-fase 3.11 → FASE 4) ─────────────────
// Productor único del símbolo desde 3.11. El bridge sobrevive porque el
// router central (src/core/router.js) y el subgrafo guardian de index.html
// (IIFE inline) consumen window.renderRitualesGrupo.

if (typeof window !== 'undefined') {
  window.renderRitualesGrupo = renderRitualesGrupo
}
